import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  TextField,
  IconButton,
  Skeleton,
  Chip,
  Divider,
  Alert,
  Tooltip,
  Slider,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import TuneIcon from "@mui/icons-material/Tune";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SyncIcon from "@mui/icons-material/Sync";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ReplayIcon from "@mui/icons-material/Replay";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import EditIcon from "@mui/icons-material/DocumentScanner";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import SpeedIcon from "@mui/icons-material/Speed";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { toast } from "react-toastify";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { Preview } from "@creatomate/preview";
import api from "../../../conf/api";
import MRSSThumbnailCreator from "./MRSSThumbnailCreator";

// m:ss formatter shared by the main dialog (e.g. TikTok cover frame readout)
const formatSeconds = (secs) => {
  const s = Math.max(0, Math.floor(Number(secs) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// Playback speed bounds — Creatomate only accepts element speed between
// 50 and 200 (percent), i.e. 0.5x–2x. Values outside make setSource() throw.
const SPEED_MIN = 0.5;
const SPEED_MAX = 2;
const SPEED_MARKS = [0.5, 0.75, 1, 1.25, 1.5, 2].map((v) => ({ value: v, label: `${v}x` }));

const DIALOG_STEPS = ["Select Template", "Edit Captions & Preview"];

// ── Track (layer) reservations ───────────────────────────────────────────────
// These tracks are owned by non-caption layers and must never be used by a
// caption, otherwise the video / narration / music / per-caption audio would be
// disturbed. Captions may only go on empty tracks or existing caption tracks.
const TRACK_NARRATION = 10; // single voiceover track
const TRACK_BG_MUSIC = 11; // background music track
const TRACK_CAPTION_AUDIO_BASE = 20; // per-caption TTS audio: 20, 21, 22, …
const DEFAULT_CAPTION_TRACK = 3; // fallback caption/text track

// ── Default style fallback (used when template has no text element) ──────────
const DEFAULT_CAPTION_STYLE = {
  x: "50%",
  y: "88%",
  width: "85%",
  x_anchor: "50%",
  y_anchor: "50%",
  font_size: "5 vmin",
  font_weight: "600",
  color: "#FFFFFF",
  background_color: "rgba(0,0,0,0.65)",
  background_x_padding: "8%",
  background_y_padding: "5%",
  background_border_radius: "25%",
  text_align: "center",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

// Search element tree for the first text element whose name matches caption
// priority names, falling back to the first text element found anywhere.
const findCaptionElement = (elements) => {
  if (!elements) return null;

  const hit = elements.find((el) => el.type === "text");
  if (hit) return hit;

  for (const el of elements) {
    if (el.type === "text") return el;
    if (el.elements) {
      const deep = findCaptionElement(el.elements);
      if (deep) return deep;
    }
  }
  return null;
};

const extractCaptionStyle = (el) => {
  if (!el) return { ...DEFAULT_CAPTION_STYLE };
  return {
    x: el.x ?? DEFAULT_CAPTION_STYLE.x,
    y: el.y ?? DEFAULT_CAPTION_STYLE.y,
    width: el.width ?? DEFAULT_CAPTION_STYLE.width,
    x_anchor: el.x_anchor ?? DEFAULT_CAPTION_STYLE.x_anchor,
    y_anchor: el.y_anchor ?? DEFAULT_CAPTION_STYLE.y_anchor,
    font_family: el.font_family ?? null,
    font_size: el.font_size ?? DEFAULT_CAPTION_STYLE.font_size,
    font_weight: el.font_weight ?? DEFAULT_CAPTION_STYLE.font_weight,
    font_style: el.font_style ?? null,
    color: el.color ?? DEFAULT_CAPTION_STYLE.color,
    background_color:
      el.background_color ?? DEFAULT_CAPTION_STYLE.background_color,
    background_x_padding:
      el.background_x_padding ?? DEFAULT_CAPTION_STYLE.background_x_padding,
    background_y_padding:
      el.background_y_padding ?? DEFAULT_CAPTION_STYLE.background_y_padding,
    background_border_radius:
      el.background_border_radius ??
      DEFAULT_CAPTION_STYLE.background_border_radius,
    text_align: el.text_align ?? DEFAULT_CAPTION_STYLE.text_align,
    letter_spacing: el.letter_spacing ?? null,
    line_height: el.line_height ?? null,
    text_transform: el.text_transform ?? null,
    shadow_color: el.shadow_color ?? null,
    shadow_blur: el.shadow_blur ?? null,
    shadow_x: el.shadow_x ?? null,
    shadow_y: el.shadow_y ?? null,
  };
};

const findVideoElement = (elements) => {
  if (!elements) return null;
  for (const el of elements) {
    if (el.type === "video") return el;
    if (el.elements) {
      const found = findVideoElement(el.elements);
      if (found) return found;
    }
  }
  return null;
};

const findElementByName = (elements, name) => {
  if (!elements) return null;
  for (const el of elements) {
    if (el.name === name) return el;
    if (el.elements) {
      const found = findElementByName(el.elements, name);
      if (found) return found;
    }
  }
  return null;
};

const findAllCaptionElements = (elements) => {
  if (!elements) return [];
  const result = [];
  for (const el of elements) {
    if (el.type === "text") result.push(el);
    if (el.elements) result.push(...findAllCaptionElements(el.elements));
  }
  return result;
};

// Collect all named image elements (logos) so we can track drag-repositioning.
const findImageElements = (elements) => {
  if (!elements) return [];
  const result = [];
  for (const el of elements) {
    if (el.type === "image" && el.name) result.push(el);
    if (el.elements) result.push(...findImageElements(el.elements));
  }
  return result;
};

// Apply a flat { elementName: { x, y, width, height } } map onto a nested element tree.
const applyPositionOverrides = (elements, overrides) => {
  if (!elements || !Object.keys(overrides).length) return elements;
  return elements.map((el) => {
    let updated = el;
    if (el.name && overrides[el.name]) {
      updated = { ...el, ...overrides[el.name] };
    }
    if (updated.elements) {
      updated = {
        ...updated,
        elements: applyPositionOverrides(updated.elements, overrides),
      };
    }
    return updated;
  });
};

// Recursively removes all text elements from the element tree so template
// caption placeholders don't appear alongside our injected captions.
const removeAllCaptionElements = (elements) => {
  if (!elements) return [];
  return elements
    .filter((el) => el.type !== "text")
    .map((el) =>
      el.elements
        ? { ...el, elements: removeAllCaptionElements(el.elements) }
        : el,
    );
};

// ── Creatomate Preview Player ────────────────────────────────────────────────
// Runs in "interactable" mode so users can drag/resize caption elements.
// source        – full composed Creatomate source JSON
// duration      – total video duration in seconds (from parent video metadata)
// onTimeChange  – called with current playback time (seconds)
// onStateChange – called with (updatedSource, preview) when user drags an element
function CreatomatePreviewPlayer({
  source,
  onTimeChange,
  onStateChange,
  onPlayPauseChange,
  video_duration,
  trim_start,
  onCaptureThumbnail,
  isCapturingThumbnail,
}) {
  const containerRef = useRef(null);
  const previewRef = useRef(null);
  const isReadyRef = useRef(false);
  const isPlayingRef = useRef(false); // stable ref so timer callbacks never go stale
  const bufferingTimerRef = useRef(null); // setTimeout handle for stall detection
  const [playerStatus, setPlayerStatus] = useState("initializing"); // initializing | ready | loading | error
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(video_duration);

  // Sync display duration whenever the prop changes (e.g. trim range updated)
  useEffect(() => {
    if (video_duration > 0) setDuration(video_duration);
  }, [video_duration]);

  // Keep stable refs to the latest callbacks/values so the SDK closure never goes stale
  const onStateChangeRef = useRef(onStateChange);
  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  // Initialise once when the component mounts
  useEffect(() => {
    if (!containerRef.current) return;
    const preview = new Preview(
      containerRef.current,
      "interactive", // ← enables drag-to-reposition
      process.env.REACT_APP_CREATOMATE_PUBLIC_TOKEN,
    );

    // ── Buffering stall-detector helpers ────────────────────────────────────
    // If onTimeChange stops firing for 600 ms while the player is supposed to
    // be playing, we assume the video is buffering and show the spinner.
    const clearBufTimer = () => {
      if (bufferingTimerRef.current) {
        clearTimeout(bufferingTimerRef.current);
        bufferingTimerRef.current = null;
      }
    };
    const scheduleBufCheck = () => {
      clearBufTimer();
      bufferingTimerRef.current = setTimeout(() => {
        if (isPlayingRef.current) setIsBuffering(true);
      }, 600);
    };

    preview.onReady = () => {
      isReadyRef.current = true;
      setPlayerStatus("ready");
    };

    preview.onPlay = () => {
      isPlayingRef.current = true;
      setIsPlaying(true);
      scheduleBufCheck();
      onPlayPauseChange?.(true);
    };

    preview.onPause = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
      setIsBuffering(false);
      clearBufTimer();
      onPlayPauseChange?.(false);
    };

    preview.onTimeChange = (time) => {
      setCurrentTime(time);
      onTimeChange?.(time);
      if (isPlayingRef.current) {
        setIsBuffering(false); // time is advancing — not buffering
        scheduleBufCheck(); // reschedule the stall check
      }
    };

    // Fires on every user interaction (drag, resize, text edit in the canvas)
    preview.onStateChange = () => {
      const updatedSource = preview.getSource();
      onStateChangeRef.current?.(updatedSource, preview);
    };

    preview.onError = (err) => {
      console.error("Creatomate preview error:", err);
      setPlayerStatus("error");
      setIsBuffering(false);
      clearBufTimer();
    };

    previewRef.current = preview;

    return () => {
      clearBufTimer();
      previewRef.current = null;
      isReadyRef.current = false;
      isPlayingRef.current = false;
      setIsPlaying(false);
      setIsBuffering(false);
      preview.dispose?.();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Push new source whenever it changes and the player is ready
  useEffect(() => {
    if (!source || !isReadyRef.current || !previewRef.current) return;

    setPlayerStatus("loading");
    setIsPlaying(false);
    setIsBuffering(false);
    setCurrentTime(0);
    // setDuration(0);
    console.debug("[Creatomate] setSource →", source);
    previewRef.current
      .setSource(source)
      .then(() => setPlayerStatus("ready"))
      .catch((err) => {
        console.error("[Creatomate] setSource failed:", err, source);
        setPlayerStatus("error");
      });
  }, [source]);

  const handlePlayPause = () => {
    if (!previewRef.current) return;
    if (isPlaying) {
      previewRef.current.pause();
    } else {
      previewRef.current.play();
    }
  };

  const handleRestart = () => {
    if (!previewRef.current) return;
    previewRef.current.setTime(0); // composition always starts at 0; trim_start is source-only
    previewRef.current.play();
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const controlsDisabled =
    playerStatus === "initializing" || playerStatus === "error" || !source;

  const handleCaptureThumbnail = async () => {
    if (!previewRef.current) return;
    // Pause so the frame is stable, then capture at exactly the displayed currentTime.
    if (isPlaying) {
      await previewRef.current.pause();
    }
    // Grab the SDK's LIVE source (matches exactly what is on screen, incl. live drags
    // and the current composition duration). Using this instead of the debounced
    // React `previewSource` prevents Creatomate from clamping snapshot_time to a
    // stale/shorter composition duration.
    let liveSource = null;
    try {
      liveSource = previewRef.current.getSource();
    } catch { /* fall back to debounced source in parent */ }
    // Send the exact time shown in the readout — no seeking, no rounding.
    onCaptureThumbnail?.(currentTime, liveSource);
  };

  return (
    <Box sx={{ borderRadius: 2, overflow: "hidden", bgcolor: "#000", boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
      {/* Player area */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "16/9",
        }}
      >
        {/* The SDK renders an iframe into this div */}
        <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

        {/* Initialising overlay */}
        {playerStatus === "initializing" && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              bgcolor: "rgba(0,0,0,0.7)",
            }}
          >
            <CircularProgress size={32} sx={{ color: "white" }} />
            <Typography variant="caption" color="white">
              Loading preview…
            </Typography>
          </Box>
        )}

        {/* Source loading overlay */}
        {playerStatus === "loading" && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              bgcolor: "rgba(0,0,0,0.55)",
            }}
          >
            <CircularProgress size={32} sx={{ color: "white" }} />
            <Typography variant="caption" color="white">
              Rendering preview…
            </Typography>
          </Box>
        )}

        {/* Runtime buffering overlay */}
        {playerStatus === "ready" && isBuffering && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              bgcolor: "rgba(0,0,0,0.45)",
              pointerEvents: "none", // don't block drag interactions
            }}
          >
            <CircularProgress size={28} sx={{ color: "white" }} />
            <Typography variant="caption" color="white">
              Buffering…
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {playerStatus === "error" && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(0,0,0,0.75)",
            }}
          >
            <Typography variant="body2" color="error.light">
              Preview failed. Check console for details.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Playback controls bar */}
      <Box
        sx={{
          display: "flex", alignItems: "center", gap: 0.5,
          px: 1.5, py: 1,
          background: "linear-gradient(180deg, #111 0%, #1a1a2e 100%)",
          borderTop: "1px solid rgba(99,102,241,0.2)",
        }}
      >
        <Tooltip title={isPlaying ? "Pause" : "Play"}>
          <span>
            <IconButton size="small" onClick={handlePlayPause} disabled={controlsDisabled}
              sx={{ color: "white", "&:hover": { bgcolor: "rgba(99,102,241,0.2)" },
                "&.Mui-disabled": { color: "grey.800" }, borderRadius: 1 }}>
              {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Restart from beginning">
          <span>
            <IconButton size="small" onClick={handleRestart} disabled={controlsDisabled}
              sx={{ color: "white", "&:hover": { bgcolor: "rgba(99,102,241,0.2)" },
                "&.Mui-disabled": { color: "grey.800" }, borderRadius: 1 }}>
              <ReplayIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {/* Time readout */}
        <Box sx={{ mx: 0.5, px: 1, py: 0.25, borderRadius: 1, bgcolor: "rgba(255,255,255,0.07)" }}>
          <Typography variant="caption" sx={{ color: "#a5b4fc", userSelect: "none", fontFamily: "monospace", fontSize: 11 }}>
            {formatTime(currentTime)}
            <Box component="span" sx={{ color: "rgba(255,255,255,0.3)", mx: 0.5 }}>/</Box>
            {formatTime(duration)}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }} />
        {/* {onCaptureThumbnail ? (
          <Tooltip title={isCapturingThumbnail ? "Rendering frame via Creatomate…" : "Capture this exact frame (with captions) as thumbnail"}>
            <span>
              <Button
                size="small"
                startIcon={isCapturingThumbnail
                  ? <CircularProgress size={12} sx={{ color: "#5eead4" }} />
                  : <PhotoCameraIcon sx={{ fontSize: "14px !important" }} />}
                onClick={handleCaptureThumbnail}
                disabled={controlsDisabled || playerStatus !== "ready" || isCapturingThumbnail}
                sx={{
                  color: "#5eead4", textTransform: "none", fontSize: 11, fontWeight: 600,
                  borderRadius: 1, px: 1, py: 0.5,
                  "&:hover": { bgcolor: "rgba(13,148,136,0.2)" },
                  "&.Mui-disabled": { color: "rgba(94,234,212,0.25)" },
                }}
              >
                {isCapturingThumbnail ? "Capturing…" : "Capture Thumbnail"}
              </Button>
            </span>
          </Tooltip>
        ) : ( */}
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", userSelect: "none", fontSize: 10 }}>
            Drag elements to reposition
          </Typography>
        {/* )} */}
      </Box>
    </Box>
  );
}

// ── Main dialog ──────────────────────────────────────────────────────────────

function CreatomateVideoEditDialog({
  open,
  onClose,
  video,
  onSave,
  onThumbnailSave,
  preselectedTemplate = null,
  // TikTok cover frame: current value (seconds) + setter. When provided, a
  // "Set as Cover" control appears under the preview so the user can pick the
  // exact frame time on the FINAL composition timeline.
  coverTimestamp = undefined,
  onCoverTimestampSave = undefined,
}) {
  const [activeStep, setActiveStep] = useState(0);

  const effectiveVideoUrl = video?.raw_video_url;

  // Thumbnail capture from the video editor
  const [thumbnailEditorOpen, setThumbnailEditorOpen] = useState(false);
  const [thumbnailCapturing, setThumbnailCapturing] = useState(false);
  const [thumbnailCapturedFrame, setThumbnailCapturedFrame] = useState(null);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [captionStyle, setCaptionStyle] = useState({
    ...DEFAULT_CAPTION_STYLE,
  });
  // Keeps the style as extracted from the template so the user can reset to it
  const [templateCaptionStyle, setTemplateCaptionStyle] = useState(null);
  // Full template element objects for first / middle / last caption positions
  const [templateCaptionElements, setTemplateCaptionElements] = useState({
    first: null,
    middle: null,
    last: null,
  });

  // Captions
  // Per-caption x/y/width from user drags — keyed by caption id.
  // Falls back to captionStyle when a caption has no individual override.
  const [captionPositions, setCaptionPositions] = useState({});

  // Per-logo-element position overrides from user drags — keyed by element name.
  const [logoOverrides, setLogoOverrides] = useState({});

  const [captions, setCaptions] = useState([]);
  const [generatingCaptions, setGeneratingCaptions] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const CAPTION_PROMPT_LS_KEY = "caption_custom_prompt";
  const [customPrompt, setCustomPrompt] = useState(
    () => localStorage.getItem(CAPTION_PROMPT_LS_KEY) || "",
  );
  const [showPromptField, setShowPromptField] = useState(false);

  // Stable ref so handlePreviewStateChange (memoised with []) can read the latest captions
  const captionsRef = useRef(captions);
  useEffect(() => {
    captionsRef.current = captions;
  }, [captions]);

  // Audio sync refs — used to play TTS audio in lockstep with the Creatomate preview
  const audioRefsMap = useRef({}); // { [captionId]: HTMLAudioElement }
  const previewPlayingRef = useRef(false);
  const lastPreviewTimeRef = useRef(0);

  // Video settings
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [videoVolume, setVideoVolume] = useState(100);
  const [videoSpeed, setVideoSpeed] = useState(1); // playback rate (1 = normal)
  // Text mirror of videoSpeed for the manual decimal input — lets the user type
  // intermediate values ("1.", "0.7") without the field snapping mid-keystroke.
  const [speedInput, setSpeedInput] = useState("1");
  useEffect(() => {
    setSpeedInput(String(videoSpeed));
  }, [videoSpeed]);

  // TTS (ElevenLabs)
  // captionAudios: { [captionId]: { url, audioDuration, loading, error, stale } }
  const [captionAudios, setCaptionAudios] = useState({});
  const [ttsVoices, setTtsVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState("");
  const [ttsVoicesLoading, setTtsVoicesLoading] = useState(false);
  const [bulkTtsLoading, setBulkTtsLoading] = useState(false);
  const [captionAudioSpeeds, setCaptionAudioSpeeds] = useState({}); // { [captionId]: number }
  // Stable ref so syncAudiosToTime (memoised with []) can read latest speeds
  const captionAudioSpeedsRef = useRef({});
  useEffect(() => {
    captionAudioSpeedsRef.current = captionAudioSpeeds;
  }, [captionAudioSpeeds]);

  // Collapsible sections
  const [videoSettingsOpen, setVideoSettingsOpen] = useState(true);
  const [bgMusicOpen, setBgMusicOpen] = useState(false);

  // Video Narration (AI story → ElevenLabs TTS → single audio track)
  const [narrationText, setNarrationText] = useState("");
  const [storyGenerating, setStoryGenerating] = useState(false);
  const [narrationAudio, setNarrationAudio] = useState(null); // { url, duration, stale }
  const [narrationAudioGenerating, setNarrationAudioGenerating] =
    useState(false);
  const [narrationVolume, setNarrationVolume] = useState(85); // 0–100
  const [narrationSpeed, setNarrationSpeed] = useState(1); // playback rate
  const narrationSpeedRef = useRef(1);
  useEffect(() => {
    narrationSpeedRef.current = narrationSpeed;
  }, [narrationSpeed]);
  const narrationAudioRef = useRef(null);

  // Background Music (ElevenLabs Sound Effects)
  const [musicTab, setMusicTab] = useState("history"); // "generate" | "history"
  const [musicHistory, setMusicHistory] = useState([]);
  const [musicHistoryLoading, setMusicHistoryLoading] = useState(false);
  const [bgMusic, setBgMusic] = useState(null); // { url, duration, prompt }
  const [bgMusicVolume, setBgMusicVolume] = useState(60); // 0–100
  const [bgMusicSpeed, setBgMusicSpeed] = useState(1); // playback rate
  const bgMusicSpeedRef = useRef(1);
  useEffect(() => {
    bgMusicSpeedRef.current = bgMusicSpeed;
  }, [bgMusicSpeed]);
  const [musicPrompt, setMusicPrompt] = useState("");
  const [musicGenerating, setMusicGenerating] = useState(false);
  const bgMusicAudioRef = useRef(null);

  // Trimmed clip length in SOURCE seconds (what trim_start/trim_duration use)
  const sourceClipDuration =
    trimEnd > trimStart && trimEnd > 0 ? trimEnd - trimStart : videoDuration;

  // Effective wall-clock duration of the clip after playback speed is applied
  // (captions, narration and music must fit within this)
  const clipDuration = sourceClipDuration / videoSpeed;

  // ── Track reservations ─────────────────────────────────────────────────────
  // Set of tracks that hold non-caption layers (base video, logos, shapes) plus
  // the audio tracks we inject. Captions are blocked from these so they never
  // disturb the video, narration, music, or per-caption audio.
  const reservedTracks = useMemo(() => {
    const reserved = new Set([TRACK_NARRATION, TRACK_BG_MUSIC]);
    const els = selectedTemplate?.source?.elements || [];
    // Top-level non-text template elements (logos, shapes, base video, etc.)
    els.forEach((el) => {
      if (el && el.type !== "text" && el.track != null) {
        reserved.add(Number(el.track));
      }
    });
    // Always protect the main video element's track even if it is nested
    const videoEl =
      findElementByName(els, "Video-DHM") || findVideoElement(els);
    if (videoEl?.track != null) reserved.add(Number(videoEl.track));
    return reserved;
  }, [selectedTemplate]);

  // A caption may use a track only if it isn't reserved and isn't in the
  // per-caption audio band (≥ 20). Empty tracks and existing caption tracks pass.
  const isCaptionTrackAllowed = useCallback(
    (track) => {
      const t = Number(track);
      if (!Number.isInteger(t) || t < 1) return false;
      if (t >= TRACK_CAPTION_AUDIO_BASE) return false;
      return !reservedTracks.has(t);
    },
    [reservedTracks],
  );

  // Sorted list of reserved tracks for display in the UI helper text.
  const reservedTracksLabel = useMemo(
    () =>
      [...reservedTracks]
        .filter((t) => t < TRACK_CAPTION_AUDIO_BASE)
        .sort((a, b) => a - b)
        .join(", "),
    [reservedTracks],
  );

  // Default track for new captions: reuse the template's caption track when it
  // is allowed, otherwise pick the lowest free (non-reserved) track.
  const defaultCaptionTrack = useMemo(() => {
    const textTrack = templateCaptionElements.first?.track;
    if (textTrack != null && isCaptionTrackAllowed(textTrack)) {
      return Number(textTrack);
    }
    for (let t = 1; t < TRACK_CAPTION_AUDIO_BASE; t++) {
      if (isCaptionTrackAllowed(t)) return t;
    }
    return DEFAULT_CAPTION_TRACK;
  }, [templateCaptionElements, isCaptionTrackAllowed]);

  // Debounced preview source (passed to CreatomatePreviewPlayer)
  const [previewSource, setPreviewSource] = useState(null);

  // Set to true when a captionStyle update originates from a user drag in the
  // preview so the debounce skips calling setSource (SDK already has the state)
  const interactionSkipRef = useRef(false);

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  useEffect(() => {
    // Only fetch template gallery when no preselected template is provided
    if (open && !preselectedTemplate && templates.length === 0)
      fetchTemplates();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setSelectedTemplate(null);
      setCaptionStyle({ ...DEFAULT_CAPTION_STYLE });
      setTemplateCaptionStyle(null);
      setTemplateCaptionElements({ first: null, middle: null, last: null });
      setCaptions([]);
      setCaptionPositions({});
      setLogoOverrides({});
      setCurrentTime(0);
      setPreviewSource(null);
      setTrimStart(0);
      setTrimEnd(0);
      setVideoVolume(100);
      setVideoSpeed(1);
      setCaptionAudios({});
      setSelectedVoice("");
      // Stop and release all audio elements
      Object.values(audioRefsMap.current).forEach((a) => {
        a.pause();
        a.src = "";
      });
      audioRefsMap.current = {};
      previewPlayingRef.current = false;
      // Stop and release narration audio
      if (narrationAudioRef.current) {
        narrationAudioRef.current.pause();
        narrationAudioRef.current.src = "";
        narrationAudioRef.current = null;
      }
      setNarrationText("");
      setNarrationAudio(null);
      // Stop and release background music
      if (bgMusicAudioRef.current) {
        bgMusicAudioRef.current.pause();
        bgMusicAudioRef.current.src = "";
        bgMusicAudioRef.current = null;
      }
      setBgMusic(null);
      setMusicPrompt("");
      setMusicHistory([]);
    } else if (preselectedTemplate) {
      // Skip template gallery — jump straight to caption editing with brand template
      setActiveStep(1);
      setSelectedTemplate(preselectedTemplate);
      if (preselectedTemplate.source) {
        const allCaptionEls = preselectedTemplate.source.elements
          ? findAllCaptionElements(preselectedTemplate.source.elements)
          : [];
        const firstEl = allCaptionEls[0] ?? null;
        const middleEl = allCaptionEls[1] ?? allCaptionEls[0] ?? null;
        const lastEl =
          allCaptionEls[allCaptionEls.length - 1] ?? allCaptionEls[0] ?? null;
        setTemplateCaptionElements({
          first: firstEl,
          middle: middleEl,
          last: lastEl,
        });
        const style = extractCaptionStyle(firstEl);
        setCaptionStyle(style);
        setTemplateCaptionStyle(style);
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // (voices fetch effect is placed after fetchTtsVoices is defined)

  // ── Load video metadata as soon as step 2 opens ────────────────────────────
  // Creates a throw-away <video> element to read duration without rendering it.
  useEffect(() => {
    if (activeStep !== 1 || !effectiveVideoUrl) return;

    const vid = document.createElement("video");
    vid.preload = "metadata";
    vid.src = effectiveVideoUrl?.replace(
      "dvkno8lo5mn14.cloudfront.net",
      "wooglobe.s3.us-west-2.amazonaws.com",
    );

    vid.onloadedmetadata = () => {
      const dur = vid.duration;
      setVideoDuration(dur);
      setTrimEnd((prev) => (prev === 0 ? dur : prev));
      vid.src = "";
    };

    vid.onerror = (e) => {
      console.warn("Failed to load video metadata:", e);
      // setVideoDuration(60);
      // setTrimEnd((prev) => (prev === 0 ? 60 : prev));
    };

    return () => {
      vid.src = "";
    };
  }, [activeStep, effectiveVideoUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // NOTE: Trimming no longer auto-clamps or removes captions. The user has full
  // control over each caption's start time and duration, so changing the trim
  // range leaves their caption timings untouched. Captions whose start falls
  // outside the rendered clip simply won't appear in the final video.

  // ── Debounce: rebuild preview source whenever captions or style change ──────
  // Skipped when the change originated from a user drag in the preview itself
  // (the SDK already holds the correct state — no need to call setSource again).
  useEffect(() => {
    if (activeStep !== 1 || !selectedTemplate?.source) return;
    if (interactionSkipRef.current) {
      interactionSkipRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      setPreviewSource(buildRenderSource());
    }, 700);

    return () => clearTimeout(timer);
  }, [
    captions,
    captionStyle,
    captionPositions,
    logoOverrides,
    selectedTemplate,
    effectiveVideoUrl,
    activeStep,
    templateCaptionElements,
    trimStart,
    trimEnd,
    videoVolume,
    videoSpeed,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handle drag / resize from the interactable preview ────────────────────
  // Tracks per-caption positions (Caption_N → captionPositions[id]) and
  // per-logo positions (image elements → logoOverrides[name]) independently
  // so dragging one element never disturbs the others.
  const handlePreviewStateChange = useCallback((updatedSource) => {
    if (!updatedSource?.elements) return;

    interactionSkipRef.current = true;

    // ── Per-caption position tracking ──
    const captionEls = updatedSource.elements.filter((el) =>
      el.name?.startsWith("Caption_"),
    );
    if (captionEls.length > 0) {
      setCaptionPositions((prev) => {
        const next = { ...prev };
        captionEls.forEach((el) => {
          const idx = parseInt(el.name.split("_")[1], 10);
          const cap = captionsRef.current[idx];
          if (!cap) return;
          next[cap.id] = {
            ...(prev[cap.id] || {}),
            ...(el.x !== undefined && { x: el.x }),
            ...(el.y !== undefined && { y: el.y }),
            ...(el.width !== undefined && { width: el.width }),
          };
        });
        return next;
      });
    }

    // ── Logo (image element) position tracking ──
    const imageEls = updatedSource.elements.filter(
      (el) => el.type === "image" && el.name,
    );
    if (imageEls.length > 0) {
      setLogoOverrides((prev) => {
        const next = { ...prev };
        imageEls.forEach((el) => {
          next[el.name] = {
            ...(prev[el.name] || {}),
            ...(el.x !== undefined && { x: el.x }),
            ...(el.y !== undefined && { y: el.y }),
            ...(el.width !== undefined && { width: el.width }),
            ...(el.height !== undefined && { height: el.height }),
          };
        });
        return next;
      });
    }
  }, []);

  // ── ElevenLabs TTS ─────────────────────────────────────────────────────────

  const fetchTtsVoices = useCallback(async () => {
    setTtsVoicesLoading(true);
    try {
      const res = await api.get("/mrss/tts/voices/");
      const voices = Array.isArray(res.data) ? res.data : [];
      if (!voices.length) {
        const msg =
          res.data?.error ||
          "No voices returned — check ELEVENLABS_API_KEY on the server";
        toast.warning(msg);
      }
      setTtsVoices(voices);
      setSelectedVoice((prev) => prev || voices[0]?.voice_id || "");
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to load ElevenLabs voices";
      toast.error(msg);
    } finally {
      setTtsVoicesLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch voices once when the user reaches the caption editor step
  useEffect(() => {
    if (activeStep === 1 && open && ttsVoices.length === 0) fetchTtsVoices();
  }, [activeStep, open, fetchTtsVoices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build/rebuild HTMLAudio objects whenever audio URLs change.
  // Uses refs only so the sync callbacks can remain stable.
  useEffect(() => {
    Object.values(audioRefsMap.current).forEach((a) => {
      a.pause();
      a.src = "";
    });
    audioRefsMap.current = {};
    captions.forEach((cap) => {
      const ad = captionAudios[cap.id];
      if (ad?.url && !ad?.stale) {
        const audio = new Audio(ad.url);
        audio.preload = "auto";
        audio.playbackRate = captionAudioSpeeds[cap.id] ?? 1;
        audioRefsMap.current[cap.id] = audio;
      }
    });
  }, [captionAudios, captions]);

  // Rebuild background music Audio object whenever bgMusic URL changes.
  useEffect(() => {
    if (bgMusicAudioRef.current) {
      bgMusicAudioRef.current.pause();
      bgMusicAudioRef.current.src = "";
      bgMusicAudioRef.current = null;
    }
    if (bgMusic?.url) {
      const audio = new Audio(bgMusic.url);
      audio.preload = "auto";
      audio.loop = true;
      audio.volume = bgMusicVolume / 100;
      audio.playbackRate = bgMusicSpeed;
      bgMusicAudioRef.current = audio;
    }
  }, [bgMusic]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep bg music volume + speed in sync with sliders.
  useEffect(() => {
    if (bgMusicAudioRef.current)
      bgMusicAudioRef.current.volume = bgMusicVolume / 100;
  }, [bgMusicVolume]);

  useEffect(() => {
    if (bgMusicAudioRef.current)
      bgMusicAudioRef.current.playbackRate = bgMusicSpeed;
  }, [bgMusicSpeed]);

  // Rebuild narration Audio object when URL changes.
  useEffect(() => {
    if (narrationAudioRef.current) {
      narrationAudioRef.current.pause();
      narrationAudioRef.current.src = "";
      narrationAudioRef.current = null;
    }
    if (narrationAudio?.url && !narrationAudio?.stale) {
      const audio = new Audio(narrationAudio.url);
      audio.preload = "auto";
      audio.volume = narrationVolume / 100;
      audio.playbackRate = narrationSpeed;
      narrationAudioRef.current = audio;
    }
  }, [narrationAudio]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (narrationAudioRef.current)
      narrationAudioRef.current.volume = narrationVolume / 100;
  }, [narrationVolume]);

  useEffect(() => {
    if (narrationAudioRef.current)
      narrationAudioRef.current.playbackRate = narrationSpeed;
  }, [narrationSpeed]);

  // Keep caption audio playback rate in sync.
  useEffect(() => {
    Object.entries(audioRefsMap.current).forEach(([id, a]) => {
      a.playbackRate = captionAudioSpeeds[id] ?? 1;
    });
  }, [captionAudioSpeeds]);

  // Seek + play/pause each audio to match the preview's current time.
  // All values read from refs → function is stable (empty deps array).
  const syncAudiosToTime = useCallback((t, forceSeek = false) => {
    captionsRef.current.forEach((cap) => {
      const audio = audioRefsMap.current[cap.id];
      if (!audio) return;
      const speed = captionAudioSpeedsRef.current[cap.id] ?? 1;
      const relTime = t - cap.startTime;
      const audioDur = isFinite(audio.duration) ? audio.duration : 99;
      // At `speed` rate the audio file lasts audioDur/speed wall-clock seconds
      const effectiveWallDur = audioDur / speed;
      const shouldPlay =
        previewPlayingRef.current && relTime >= 0 && relTime < effectiveWallDur;

      if (shouldPlay) {
        if (forceSeek || audio.paused) {
          // Map wall-clock relTime → position in the audio file
          audio.currentTime = Math.min(relTime * speed, audioDur - 0.01);
          audio.play().catch(() => {});
        }
      } else if (!audio.paused) {
        audio.pause();
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePreviewTimeChange = useCallback(
    (t) => {
      setCurrentTime(t);
      const jumped = Math.abs(t - lastPreviewTimeRef.current) > 0.5;
      lastPreviewTimeRef.current = t;
      syncAudiosToTime(t, jumped);
      // Sync background music — looped, seeks within loop cycle
      const bgAudio = bgMusicAudioRef.current;
      if (bgAudio && isFinite(bgAudio.duration) && bgAudio.duration > 0) {
        const bgSpeed = bgMusicSpeedRef.current;
        if (jumped) bgAudio.currentTime = (t * bgSpeed) % bgAudio.duration;
        if (previewPlayingRef.current && bgAudio.paused)
          bgAudio.play().catch(() => {});
      }
      // Sync narration — single-play voiceover, speed-aware
      const narAudio = narrationAudioRef.current;
      if (narAudio) {
        const narSpeed = narrationSpeedRef.current;
        const narFileDur = isFinite(narAudio.duration)
          ? narAudio.duration
          : null;
        const narEffectiveDur =
          narFileDur != null ? narFileDur / narSpeed : Infinity;
        if (jumped && narFileDur != null)
          narAudio.currentTime = Math.min(t * narSpeed, narFileDur - 0.01);
        if (previewPlayingRef.current && t < narEffectiveDur) {
          if (narAudio.paused) narAudio.play().catch(() => {});
        } else if (
          previewPlayingRef.current &&
          t >= narEffectiveDur &&
          !narAudio.paused
        ) {
          narAudio.pause();
        } else if (!previewPlayingRef.current && !narAudio.paused) {
          narAudio.pause();
        }
      }
    },
    [syncAudiosToTime],
  );

  const handlePreviewPlayPause = useCallback(
    (playing) => {
      previewPlayingRef.current = playing;
      if (playing) {
        syncAudiosToTime(lastPreviewTimeRef.current, true);
        const bgAudio = bgMusicAudioRef.current;
        if (bgAudio) {
          if (isFinite(bgAudio.duration) && bgAudio.duration > 0)
            bgAudio.currentTime =
              (lastPreviewTimeRef.current * bgMusicSpeedRef.current) %
              bgAudio.duration;
          bgAudio.play().catch(() => {});
        }
        const narAudio = narrationAudioRef.current;
        if (narAudio) {
          const narSpeed = narrationSpeedRef.current;
          const narFileDur = isFinite(narAudio.duration)
            ? narAudio.duration
            : null;
          const t = lastPreviewTimeRef.current;
          if (narFileDur != null && t < narFileDur / narSpeed) {
            narAudio.currentTime = Math.min(t * narSpeed, narFileDur - 0.01);
            narAudio.play().catch(() => {});
          }
        }
      } else {
        Object.values(audioRefsMap.current).forEach((a) => a.pause());
        bgMusicAudioRef.current?.pause();
        narrationAudioRef.current?.pause();
      }
    },
    [syncAudiosToTime],
  );

  const generateCaptionTts = async (captionId, text) => {
    if (!text?.trim()) return;
    setCaptionAudios((prev) => ({
      ...prev,
      [captionId]: { ...prev[captionId], loading: true, error: null },
    }));
    try {
      const res = await api.post("/mrss/tts/generate/", {
        text,
        voice_id: selectedVoice,
        caption_id: captionId,
      });
      setCaptionAudios((prev) => ({
        ...prev,
        [captionId]: {
          url: res.data.url,
          audioDuration: res.data.audio_duration,
          loading: false,
          error: null,
          stale: false,
        },
      }));
    } catch (err) {
      const msg = err?.response?.data?.error || "Audio generation failed";
      setCaptionAudios((prev) => ({
        ...prev,
        [captionId]: { ...prev[captionId], loading: false, error: msg },
      }));
      toast.error(msg);
    }
  };

  const generateAllTts = async () => {
    const pending = captions.filter((c) => c.text?.trim());
    if (!pending.length) return;

    setBulkTtsLoading(true);
    // Mark all as loading
    setCaptionAudios((prev) => {
      const next = { ...prev };
      pending.forEach((c) => {
        next[c.id] = { ...next[c.id], loading: true, error: null };
      });
      return next;
    });

    try {
      const res = await api.post("/mrss/tts/bulk/", {
        captions: pending.map((c) => ({ id: c.id, text: c.text })),
        voice_id: selectedVoice,
      });

      setCaptionAudios((prev) => {
        const next = { ...prev };
        // Apply successes
        (res.data.results || []).forEach((r) => {
          next[r.caption_id] = {
            url: r.url,
            audioDuration: r.audio_duration,
            loading: false,
            error: null,
            stale: false,
          };
        });
        // Apply errors
        (res.data.errors || []).forEach((e) => {
          next[e.caption_id] = {
            ...next[e.caption_id],
            loading: false,
            error: e.error,
          };
        });
        return next;
      });

      const ok = (res.data.results || []).length;
      const fail = (res.data.errors || []).length;
      if (ok > 0)
        toast.success(
          `Generated audio for ${ok} caption${ok !== 1 ? "s" : ""}`,
        );
      if (fail > 0)
        toast.warning(
          `${fail} caption${fail !== 1 ? "s" : ""} failed — check individual errors`,
        );
    } catch (err) {
      toast.error(err?.response?.data?.error || "Bulk audio generation failed");
      // Clear loading flags
      setCaptionAudios((prev) => {
        const next = { ...prev };
        pending.forEach((c) => {
          if (next[c.id]?.loading)
            next[c.id] = { ...next[c.id], loading: false };
        });
        return next;
      });
    } finally {
      setBulkTtsLoading(false);
    }
  };

  // ── Background Music ───────────────────────────────────────────────────────

  const generateBackgroundMusic = async () => {
    if (!musicPrompt.trim()) {
      toast.warning("Enter a music prompt first");
      return;
    }
    setMusicGenerating(true);
    try {
      const res = await api.post("/mrss/music/generate/", {
        prompt: musicPrompt.trim(),
      });
      setBgMusic({
        url: res.data.url,
        duration: res.data.duration,
        prompt: res.data.prompt,
      });
      toast.success("Background music generated");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Music generation failed");
    } finally {
      setMusicGenerating(false);
    }
  };

  const fetchMusicHistory = async () => {
    setMusicHistoryLoading(true);
    try {
      const res = await api.get("/mrss/music/history/");
      setMusicHistory(Array.isArray(res.data) ? res.data : []);
      if (!res.data?.length)
        toast.info("No sound effects found in your ElevenLabs history");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to load music history");
    } finally {
      setMusicHistoryLoading(false);
    }
  };

  const selectHistoryMusic = async (item) => {
    try {
      const res = await api.post("/mrss/music/select-history/", {
        history_item_id: item.id,
      });
      setBgMusic({
        url: res.data.url,
        duration: res.data.duration,
        prompt: item.text,
      });
      toast.success("Music loaded from history");
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Failed to load music from history",
      );
    }
  };

  // ── Video Narration ────────────────────────────────────────────────────────

  const generateStory = async () => {
    setStoryGenerating(true);
    try {
      const res = await api.post("/mrss/story/generate/", {
        title: video?.title || video?.video_title || "",
        description: video?.description || "",
        duration_seconds: Math.floor(clipDuration),
      });
      setNarrationText(res.data.story || "");
      setNarrationAudio((prev) => (prev ? { ...prev, stale: true } : null));
    } catch (err) {
      toast.error(err?.response?.data?.error || "Story generation failed");
    } finally {
      setStoryGenerating(false);
    }
  };

  const generateNarrationAudio = async () => {
    if (!narrationText.trim()) {
      toast.warning("Write or generate a narration script first");
      return;
    }
    if (!selectedVoice) {
      toast.warning("Select a voice in the Text-to-Speech section first");
      return;
    }
    setNarrationAudioGenerating(true);
    try {
      const res = await api.post("/mrss/tts/generate/", {
        text: narrationText.trim(),
        voice_id: selectedVoice,
        caption_id: "narration",
      });
      setNarrationAudio({
        url: res.data.url,
        duration: res.data.audio_duration,
        stale: false,
      });
      toast.success("Narration audio generated");
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Narration audio generation failed",
      );
    } finally {
      setNarrationAudioGenerating(false);
    }
  };

  // ── Template fetch ─────────────────────────────────────────────────────────

  const fetchTemplates = async () => {
    setTemplatesLoading(true);

    try {
      // 1. Get all templates via backend proxy (API key stays server-side)
      const res = await api.get("/mrss/creatomate-templates/");

      const templates = res.data || [];

      // 2. Fetch full details for each template by ID
      const detailedTemplates = await Promise.all(
        templates.map(async (template) => {
          try {
            const detailRes = await api.get(
              `/mrss/creatomate-templates/${template.id}/`,
            );
            return { ...template, ...detailRes.data };
          } catch (err) {
            console.warn(`Failed to load template ${template.id}`, err);
            return template;
          }
        }),
      );

      // 3. Set final enriched templates
      setTemplates(detailedTemplates);
    } catch (err) {
      toast.error("Failed to load Creatomate templates.");
    } finally {
      setTemplatesLoading(false);
    }
  };

  // When a template card is clicked: ensure we have its full source JSON, then
  // extract the caption element's style so preview + render match exactly.
  const handleSelectTemplate = async (tmpl) => {
    setSelectedTemplate(tmpl);

    let source = tmpl.source;

    if (!source) {
      try {
        const res = await api.get(`/mrss/creatomate-templates/${tmpl.id}/`);
        source = res.data.source;
        const updatedTmpl = { ...tmpl, source };
        setTemplates((prev) =>
          prev.map((t) => (t.id === tmpl.id ? updatedTmpl : t)),
        );
        setSelectedTemplate(updatedTmpl);
      } catch {
        toast.warning(
          "Could not load template details — using default caption style.",
        );
      }
    }

    const allCaptionEls = source?.elements
      ? findAllCaptionElements(source.elements)
      : [];
    const firstEl = allCaptionEls[0] ?? null;
    const middleEl = allCaptionEls[1] ?? allCaptionEls[0] ?? null;
    const lastEl =
      allCaptionEls[allCaptionEls.length - 1] ?? allCaptionEls[0] ?? null;
    setTemplateCaptionElements({
      first: firstEl,
      middle: middleEl,
      last: lastEl,
    });

    // Keep captionStyle in sync for the badge display; clear any drag overrides
    const firstStyle = extractCaptionStyle(firstEl);
    setCaptionStyle(firstStyle);
    setTemplateCaptionStyle(firstStyle);
    setCaptionPositions({});
    setLogoOverrides({});
  };

  // ── Source builder ─────────────────────────────────────────────────────────

  const buildRenderSource = useCallback(() => {
    if (!selectedTemplate?.source) return null;

    const source = JSON.parse(JSON.stringify(selectedTemplate.source));

    // Inject video URL — prefer the named "Video-DHM" element, fall back to
    // the first video element, or prepend a new one if none exists.
    const resolvedVideoUrl = effectiveVideoUrl?.replace(
      "dvkno8lo5mn14.cloudfront.net",
      "wooglobe.s3.us-west-2.amazonaws.com",
    );
    const videoEl =
      findElementByName(source.elements, "Video-DHM") ??
      findVideoElement(source.elements);
    if (videoEl) {
      videoEl.source = resolvedVideoUrl;
      videoEl.time = 0; // pin element to composition start so trim_start is source-only
      videoEl.trim_start = trimStart;

      // trim is in SOURCE seconds; at `videoSpeed` rate the element occupies
      // sourceClipDur / speed wall-clock seconds on the timeline.
      const sourceClipDur = (trimEnd || videoDuration) - trimStart;
      const wallClipDur = sourceClipDur / videoSpeed;
      // Always set explicitly so Creatomate never infers duration from caption end times
      if (wallClipDur > 0) {
        videoEl.duration = wallClipDur;
        source.duration = wallClipDur;
      }
      videoEl.speed = videoSpeed * 100; // percentage, same convention as audio elements
      videoEl.volume = videoVolume;
    } else {
      source.elements = [
        {
          type: "video",
          source: resolvedVideoUrl,
          width: "100%",
          height: "100%",
          time: 0,
        },
        ...source.elements,
      ];
    }

    // Recursively remove all template placeholder text elements
    source.elements = removeAllCaptionElements(source.elements);

    // Apply any logo/image position overrides captured from user drags
    if (Object.keys(logoOverrides).length > 0) {
      source.elements = applyPositionOverrides(source.elements, logoOverrides);
    }

    // Inject captions using full template element objects as base so every
    // property (animations, effects, etc.) is preserved automatically.
    // Each caption uses its own drag position (captionPositions[id]) when
    // available, falling back to the shared captionStyle baseline.
    const captionEls = captions.map((cap, i) => {
      const templateEl =
        i === 0
          ? templateCaptionElements.first
          : i === captions.length - 1
            ? templateCaptionElements.last
            : templateCaptionElements.middle;
      const posOverride = captionPositions[cap.id];
      return {
        ...(templateEl ?? {}),
        type: "text",
        name: `Caption_${i}`,
        text: cap.text,
        time: cap.startTime,
        duration: cap.duration,
        track: cap.track ?? defaultCaptionTrack,
        x: posOverride?.x ?? captionStyle.x,
        y: posOverride?.y ?? captionStyle.y,
        width: posOverride?.width ?? captionStyle.width,
      };
    });

    // Audio elements are NOT included in the preview source — the Creatomate
    // Preview SDK only supports video/image/text and throws on audio elements.
    // Audio is injected into the render payload (buildRenderPayload) for the
    // final output. Per-caption <audio> players in the UI cover preview needs.
    source.elements = [...source.elements, ...captionEls];
    return source;
  }, [
    selectedTemplate,
    effectiveVideoUrl,
    captions,
    captionStyle,
    captionPositions,
    logoOverrides,
    templateCaptionElements,
    defaultCaptionTrack,
    trimStart,
    trimEnd,
    videoVolume,
    videoSpeed,
    videoDuration,
  ]);

  // ── Render payload builder (template-based, for the render API) ─────────────
  // Uses template_id + modifications instead of a cloned source JSON.
  // - Removes old template caption elements by name
  // - Injects new captions via elements.add (copies style from template)
  // - Patches Video-DHM via dot-notation
  const buildRenderPayload = useCallback(() => {
    if (!selectedTemplate?.id) return null;

    const resolvedVideoUrl = effectiveVideoUrl?.replace(
      "dvkno8lo5mn14.cloudfront.net",
      "wooglobe.s3.us-west-2.amazonaws.com",
    );

    // Collect names of all existing text elements to remove
    const allCaptionEls = selectedTemplate.source?.elements
      ? findAllCaptionElements(selectedTemplate.source.elements)
      : [];
    const removeable_elements = {};
    allCaptionEls.forEach((el) => {
      if (el.name) removeable_elements[el.name] = {};
    });

    // Build new caption elements — copy template style, strip id, set timing.
    // Each caption uses its own drag position when available, falling back to captionStyle.
    const captionElements = captions.map((cap, i) => {
      const templateEl =
        i === 0
          ? templateCaptionElements.first
          : i === captions.length - 1
            ? templateCaptionElements.last
            : templateCaptionElements.middle;
      const { id, ...style } = templateEl ?? {};
      const posOverride = captionPositions[cap.id];
      return {
        ...style,
        type: "text",
        name: `caption-${i + 1}`,
        text: cap.text,
        time: cap.startTime,
        duration: cap.duration,
        track: cap.track ?? defaultCaptionTrack,
        x: posOverride?.x ?? captionStyle.x,
        y: posOverride?.y ?? captionStyle.y,
        width: posOverride?.width ?? captionStyle.width,
      };
    });

    // Trimmed length in SOURCE seconds (what Video-DHM.trim_duration uses)
    const sourceClipDur =
      trimEnd > 0 && trimEnd < videoDuration
        ? trimEnd - trimStart
        : videoDuration - trimStart;
    // Wall-clock composition length after playback speed — audio overlays and
    // the composition duration are timeline values, so they use this.
    const clipDuration = sourceClipDur / videoSpeed;

    // Build dot-notation modifications for each logo/image element that was repositioned
    const logoModifications = {};
    Object.entries(logoOverrides).forEach(([name, overrides]) => {
      Object.entries(overrides).forEach(([prop, value]) => {
        logoModifications[`${name}.${prop}`] = value;
      });
    });

    // Track layout for elements.add (template elements typically use tracks 1–3):
    //   10  — narration
    //   11  — background music
    //   20+ — per-caption audio (20, 21, 22, …)
    // Caption text elements inherit their track from the template style (~3) and are kept as-is.

    // TTS audio elements to add alongside captions
    const audioElements = captions
      .filter(
        (cap) => captionAudios[cap.id]?.url && !captionAudios[cap.id]?.stale,
      )
      .map((cap, i) => ({
        type: "audio",
        name: `caption-audio-${i + 1}`,
        track: 20 + i,
        time: cap.startTime,
        source: captionAudios[cap.id].url,
        speed: (captionAudioSpeeds[cap.id] ?? 1) * 100,
      }));

    // Background music — looped and clamped to clip duration so trim is respected
    const musicElements = bgMusic?.url
      ? [
          {
            type: "audio",
            name: "background-music",
            track: 11,
            time: 0,
            source: bgMusic.url,
            duration: clipDuration,
            loop: true,
            volume: bgMusicVolume,
            speed: bgMusicSpeed * 100,
          },
        ]
      : [];

    // Narration — single-play voiceover, clamped to clip duration
    const narrationElements =
      narrationAudio?.url && !narrationAudio?.stale
        ? [
            {
              type: "audio",
              name: "narration",
              track: 10,
              time: 0,
              source: narrationAudio.url,
              duration: clipDuration,
              volume: narrationVolume,
              speed: narrationSpeed * 100,
            },
          ]
        : [];

    const modifications = {
      ...logoModifications,
      duration: clipDuration,
      "Video-DHM.source": resolvedVideoUrl,
      "Video-DHM.time": 0,
      "Video-DHM.trim_start": trimStart,
      "Video-DHM.trim_duration": sourceClipDur,
      "Video-DHM.duration": clipDuration,
      "Video-DHM.speed": videoSpeed * 100,
      ...removeable_elements,
      "elements.add": [
        ...captionElements,
        ...audioElements,
        ...musicElements,
        ...narrationElements,
      ],
    };

    modifications["Video-DHM.volume"] = videoVolume;

    return [{ template_id: selectedTemplate.id, modifications }];
  }, [
    selectedTemplate,
    effectiveVideoUrl,
    captions,
    captionStyle,
    captionPositions,
    logoOverrides,
    templateCaptionElements,
    defaultCaptionTrack,
    trimStart,
    trimEnd,
    videoVolume,
    videoSpeed,
    videoDuration,
    captionAudios,
    captionAudioSpeeds,
    bgMusic,
    bgMusicVolume,
    bgMusicSpeed,
    narrationAudio,
    narrationVolume,
    narrationSpeed,
  ]);

  // ── Caption helpers ────────────────────────────────────────────────────────

  // Returns a reusable template — dynamic values are {{tokens}}, not live data.
  const buildDefaultTemplate = () =>
    [
      `Generate captions for a {{duration}} video clip.{{trim_context}}`,
      `Title: "{{title}}"`,
      `Description: {{description}}`,
      ``,
      `Requirements:`,
      `- Space captions evenly across the full {{duration}}`,
      `- Keep each caption short (1 phrase or sentence, max 12 words)`,
      `- Use active, punchy language suitable for social media`,
      `- No overlapping captions (startTime + duration ≤ next caption's startTime)`,
    ].join("\n");

  // Resolves {{tokens}} in a template using the current video + clip state.
  const resolvePrompt = (template) => {
    const title = video?.title || video?.video_title || "";
    const description = video?.description || "";
    const totalSecs = Math.floor(clipDuration);
    const m = Math.floor(totalSecs / 60);
    const s = totalSecs % 60;
    const durationStr =
      m > 0 ? `${m} minute${m > 1 ? "s" : ""} ${s}s` : `${s} seconds`;
    const trimContext =
      trimStart > 0
        ? `\nNote: this clip starts at ${trimStart.toFixed(1)}s into the original video. Caption startTime values must still begin at 0 (= clip start).`
        : "";

    return template
      .replace(/\{\{duration\}\}/g, durationStr)
      .replace(/\{\{title\}\}/g, title || "Untitled")
      .replace(/\{\{description\}\}/g, description || "No description")
      .replace(/\{\{trim_context\}\}/g, trimContext);
  };

  const generateCaptions = async () => {
    if (!videoDuration) {
      toast.warning(
        "Video duration is still loading, please try again in a moment.",
      );
      return;
    }
    setGeneratingCaptions(true);
    try {
      const template = customPrompt.trim() || buildDefaultTemplate();
      const resolvedPrompt = resolvePrompt(template);
      const res = await api.post("/mrss/generate-captions/", {
        title: video?.title || video?.video_title || "",
        description: video?.description || "",
        video_duration: Math.floor(clipDuration),
        trim_start: trimStart,
        custom_prompt: resolvedPrompt,
      });

      const generated = res.data.captions || [];
      const clipped = clipDuration > 0 ? clipDuration : videoDuration;
      const mapped = generated
        .map((cap, i) => ({
          id: `${Date.now()}-${i}`,
          text: cap.text,
          startTime: parseFloat(
            Math.max(0, parseFloat(cap.startTime) || 0).toFixed(1),
          ),
          duration: parseFloat(
            Math.max(0.5, parseFloat(cap.duration) || 3).toFixed(1),
          ),
          track: defaultCaptionTrack,
        }))
        .filter((cap) => cap.startTime < clipped)
        .map((cap) => ({
          ...cap,
          duration: parseFloat(
            Math.max(
              0.5,
              Math.min(cap.duration, clipped - cap.startTime),
            ).toFixed(1),
          ),
        }))
        .sort((a, b) => a.startTime - b.startTime);
      setCaptions(mapped);
      setCaptionAudios({}); // new captions → old audio is stale
      toast.success(`Generated ${mapped.length} caption(s)`);
    } catch {
      toast.error("Failed to generate captions");
    } finally {
      setGeneratingCaptions(false);
    }
  };

  const addCaption = () => {
    // Start the new caption where the last-ending caption finishes; the user is
    // free to change both the start time and duration afterwards (no clip cap).
    const maxEnd =
      captions.length > 0
        ? Math.max(...captions.map((c) => c.startTime + c.duration))
        : 0;
    const startTime = parseFloat(maxEnd.toFixed(1));
    const duration = 3; // sensible default — fully editable by the user

    setCaptions((prev) => [
      ...prev,
      {
        id: `${Date.now()}`,
        text: "New caption text",
        startTime,
        duration,
        track: defaultCaptionTrack,
      },
    ]);
  };

  const updateCaption = (id, field, value) => {
    setCaptions((prev) =>
      prev.map((cap) => {
        if (cap.id !== id) return cap;

        if (field === "startTime") {
          // Free start time — the user decides exactly when the caption appears.
          // No clamping against neighbouring captions or the clip end.
          const start = Math.max(0, parseFloat(value) || 0);
          return { ...cap, startTime: parseFloat(start.toFixed(2)) };
        }

        if (field === "duration") {
          // Free duration — no upper cap against the next caption or clip end.
          // Only a tiny positive floor so Creatomate still renders the element.
          const dur = Math.max(0.1, parseFloat(value) || 0.1);
          return { ...cap, duration: parseFloat(dur.toFixed(2)) };
        }

        if (field === "track") {
          // Only allow empty/caption tracks — never the reserved video/audio/
          // music/per-caption-audio tracks.
          const t = parseInt(value, 10);
          if (!isCaptionTrackAllowed(t)) {
            toast.warning(
              `Track ${value} is reserved (video / narration / music / audio). ` +
                `Choose an empty or caption track.`,
            );
            return cap; // keep the previous track
          }
          return { ...cap, track: t };
        }

        // Mark audio stale when text changes so user knows to regenerate
        if (field === "text" && captionAudios[id]?.url) {
          setCaptionAudios((prevAudios) => ({
            ...prevAudios,
            [id]: { ...prevAudios[id], stale: true },
          }));
        }
        return { ...cap, [field]: value };
      }),
    );
  };

  const removeCaption = (id) => {
    setCaptions((prev) => prev.filter((cap) => cap.id !== id));
    setCaptionAudios((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // ── Save edit ────────────────────────────────────────────────────────────
  // Builds the Creatomate render payload and passes it to the parent.
  // The actual Creatomate API call happens server-side when the user publishes.

  const handleRenderAndSave = () => {
    const payload = buildRenderPayload();
    if (!payload) {
      toast.error("Could not build render payload — select a template first.");
      return;
    }
    onSave(payload);
    onClose();
  };

  // Derived: which caption is active at the current playback time
  const activeCaption = captions.find(
    (cap) =>
      currentTime >= cap.startTime &&
      currentTime < cap.startTime + cap.duration,
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{ sx: { minHeight: "85vh", bgcolor: "#f8fafc", borderRadius: 3, overflow: "hidden" } }}
    >
      {/* ── Gradient header ─────────────────────────────────────────────── */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          px: 3, py: 2,
          background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)",
          color: "white", position: "relative", overflow: "hidden",
        }}>
          {/* decorative circles */}
          <Box sx={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", bottom: -50, left: 80, width: 120, height: 120, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.03)", pointerEvents: "none" }} />

          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <MovieFilterIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                  Video Editor
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" sx={{ opacity: 0.7, fontFamily: "monospace" }}>
                    {video?.unique_key}
                  </Typography>
                  {video?.title || video?.video_title ? (
                    <>
                      <Typography variant="caption" sx={{ opacity: 0.4 }}>·</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }} noWrap>
                        {video?.title || video?.video_title}
                      </Typography>
                    </>
                  ) : null}
                </Box>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={1.5} flexShrink={0}>
              <Tooltip title="Close">
                <IconButton onClick={onClose} size="small"
                  sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "white", bgcolor: "rgba(255,255,255,0.15)" } }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Stepper inside header */}
          {!preselectedTemplate && (
            <Box sx={{ mt: 2 }}>
              <Stepper activeStep={activeStep}
                sx={{
                  "& .MuiStepLabel-label": { color: "rgba(255,255,255,0.5)", fontWeight: 500, "&.Mui-active": { color: "white", fontWeight: 700 }, "&.Mui-completed": { color: "rgba(255,255,255,0.7)" } },
                  "& .MuiStepIcon-root": { color: "rgba(255,255,255,0.25)", "&.Mui-active": { color: "#a5b4fc" }, "&.Mui-completed": { color: "#86efac" } },
                  "& .MuiStepConnector-line": { borderColor: "rgba(255,255,255,0.2)" },
                }}>
                {DIALOG_STEPS.map((label) => (
                  <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
              </Stepper>
            </Box>
          )}
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: "rgba(67,56,202,0.2)" }} />

      <DialogContent sx={{ position: "relative", flex: 1, bgcolor: "#f8fafc", p: { xs: 2, md: 3 } }}>
        {/* ── Step 0: Template Gallery ── */}
        {activeStep === 0 && (
          <Box>
            <Box mb={2.5} p={2} sx={{ bgcolor: "white", borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Typography variant="subtitle1" fontWeight={700} color="#312e81" gutterBottom>
                Choose a template
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select a Creatomate template for your video. Caption font, color, size and position come directly
                from the template — the preview exactly matches the final rendered output.
              </Typography>
            </Box>

            {templatesLoading ? (
              <Grid container spacing={2}>
                {[...Array(12)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Skeleton variant="rounded" height={72} sx={{ borderRadius: 2 }} />
                  </Grid>
                ))}
              </Grid>
            ) : templates.length === 0 ? (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                No templates found. Make sure{" "}
                <strong>CREATOMATE_API_KEY</strong> is set in the server
                environment.
              </Alert>
            ) : (
              <Grid container spacing={1.5}>
                {templates.map((tmpl, idx) => {
                  const isSelected = selectedTemplate?.id === tmpl.id;
                  const accentColors = ["#4338ca","#0ea5e9","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#06b6d4"];
                  const accent = accentColors[idx % accentColors.length];
                  return (
                    <Grid item xs={12} sm={6} md={3} key={tmpl.id}>
                      <Paper
                        onClick={() => handleSelectTemplate(tmpl)}
                        elevation={isSelected ? 3 : 0}
                        sx={{
                          cursor: "pointer",
                          border: "2px solid",
                          borderColor: isSelected ? accent : "#e2e8f0",
                          overflow: "hidden",
                          borderRadius: 2,
                          transition: "all 0.15s",
                          bgcolor: isSelected ? alpha(accent, 0.04) : "white",
                          position: "relative",
                          "&:hover": { boxShadow: 3, borderColor: alpha(accent, 0.7), transform: "translateY(-1px)" },
                        }}
                      >
                        {/* accent bar at top */}
                        <Box sx={{ height: 4, bgcolor: accent, opacity: isSelected ? 1 : 0.35 }} />

                        <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: alpha(accent, 0.12),
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <MovieFilterIcon sx={{ fontSize: 16, color: accent }} />
                          </Box>
                          <Box flex={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={isSelected ? 700 : 500} noWrap
                              sx={{ color: isSelected ? accent : "text.primary" }}>
                              {tmpl.name}
                            </Typography>
                          </Box>
                          {isSelected && (
                            <CheckCircleIcon sx={{ fontSize: 18, color: accent, flexShrink: 0 }} />
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

        {/* ── Step 1: Caption Editor + Creatomate Preview ── */}
        {activeStep === 1 && (
          <Grid container spacing={3}>
            {/* Left: Caption Editor */}
            <Grid item xs={12} md={5}>
              {/* ── Video Settings ── */}
              <Paper
                elevation={0}
                variant="outlined"
                sx={{ mb: 2, borderRadius: 2, overflow: "hidden", bgcolor: "white", borderColor: "#e2e8f0" }}
              >
                {/* Header — click to collapse/expand */}
                <Box
                  onClick={() => setVideoSettingsOpen((v) => !v)}
                  sx={{
                    px: 2, py: 1.25,
                    bgcolor: videoSettingsOpen ? alpha("#4338ca", 0.04) : "white",
                    borderBottom: videoSettingsOpen ? "1px solid" : "none",
                    borderColor: "#e2e8f0",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    cursor: "pointer", userSelect: "none",
                    "&:hover": { bgcolor: alpha("#4338ca", 0.06) },
                    transition: "background 0.15s",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <TuneIcon fontSize="small" sx={{ color: "#4338ca", opacity: 0.8 }} />
                    <Typography variant="subtitle2" fontWeight={700} color="#4338ca">
                      Video Settings
                    </Typography>
                  </Box>
                  {videoSettingsOpen ? (
                    <ExpandLessIcon fontSize="small" sx={{ color: "#4338ca", opacity: 0.7 }} />
                  ) : (
                    <ExpandMoreIcon fontSize="small" color="action" />
                  )}
                </Box>

                <Collapse in={videoSettingsOpen}>
                  <Box sx={{ p: 2 }}>
                    {/* Volume */}
                    <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                      {videoVolume === 0 ? (
                        <VolumeOffIcon fontSize="small" color="error" />
                      ) : (
                        <VolumeUpIcon fontSize="small" color="action" />
                      )}
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight={500}>
                          Volume
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {videoVolume === 0
                            ? "Audio muted"
                            : `${videoVolume}%`}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ px: 1 }}>
                      <Slider
                        value={videoVolume}
                        onChange={(_e, val) => setVideoVolume(val)}
                        min={0}
                        max={100}
                        step={1}
                        color={videoVolume === 0 ? "error" : "primary"}
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Playback Speed */}
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <SpeedIcon
                        fontSize="small"
                        color={videoSpeed !== 1 ? "primary" : "action"}
                      />
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight={500}>
                          Playback Speed
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {videoSpeed === 1
                            ? "Normal speed"
                            : `${videoSpeed}x — clip plays in ${clipDuration.toFixed(1)}s`}
                        </Typography>
                      </Box>
                      {videoSpeed !== 1 && (
                        <Chip
                          label={`${videoSpeed}x`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={2} px={1}>
                      <Slider
                        size="small"
                        value={videoSpeed}
                        min={SPEED_MIN}
                        max={SPEED_MAX}
                        step={0.05}
                        marks={SPEED_MARKS}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}x`}
                        onChange={(_, v) => setVideoSpeed(Math.round(v * 100) / 100)}
                        sx={{ flex: 1, mx: 1,
                          "& .MuiSlider-markLabel": { fontSize: 10 } }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Speed"
                        value={speedInput}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setSpeedInput(raw);
                          const n = Number(raw);
                          if (raw !== "" && !Number.isNaN(n) && n >= SPEED_MIN && n <= SPEED_MAX) {
                            setVideoSpeed(Math.round(n * 100) / 100);
                          }
                        }}
                        onBlur={() => {
                          // Normalize: clamp out-of-range / invalid input back to a valid speed
                          const n = Number(speedInput);
                          if (speedInput === "" || Number.isNaN(n)) {
                            setSpeedInput(String(videoSpeed));
                          } else {
                            const clamped = Math.round(Math.min(SPEED_MAX, Math.max(SPEED_MIN, n)) * 100) / 100;
                            setVideoSpeed(clamped);
                            setSpeedInput(String(clamped));
                          }
                        }}
                        inputProps={{ min: SPEED_MIN, max: SPEED_MAX, step: 0.05 }}
                        InputProps={{
                          endAdornment: (
                            <Typography variant="caption" color="text.secondary">x</Typography>
                          ),
                        }}
                        sx={{ width: 110, flexShrink: 0 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.disabled" sx={{ px: 1 }}>
                      {SPEED_MIN}x – {SPEED_MAX}x — type any value, e.g. 1.37
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {/* Trim */}
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <ContentCutIcon
                        fontSize="small"
                        color={
                          trimStart > 0 || trimEnd < videoDuration
                            ? "primary"
                            : "action"
                        }
                      />
                      <Typography variant="body2" fontWeight={500}>
                        Trim Video
                      </Typography>
                      {(trimStart > 0 ||
                        (trimEnd > 0 && trimEnd < videoDuration)) && (
                        <Chip
                          label={`${(trimEnd - trimStart).toFixed(1)}s clip`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ ml: "auto", height: 20, fontSize: 11 }}
                        />
                      )}
                    </Box>

                    <Box sx={{ px: 1 }}>
                      <Slider
                        value={[trimStart, trimEnd || videoDuration]}
                        onChange={(_e, val) => {
                          setTrimStart(val[0]);
                          setTrimEnd(val[1]);
                        }}
                        min={0}
                        max={videoDuration || 100}
                        step={0.5}
                        disabled={!videoDuration}
                        disableSwap
                        sx={{
                          "& .MuiSlider-track": { border: "none" },
                          "& .MuiSlider-thumb": {
                            width: 14,
                            height: 14,
                            "&:hover, &.Mui-focusVisible": {
                              boxShadow: "0 0 0 8px rgba(25,118,210,0.16)",
                            },
                          },
                        }}
                      />
                    </Box>

                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Start (s)"
                          type="number"
                          value={trimStart}
                          inputProps={{ min: 0, max: trimEnd - 0.5, step: 0.5 }}
                          onChange={(e) => {
                            const v = Math.min(
                              parseFloat(e.target.value) || 0,
                              trimEnd - 0.5,
                            );
                            setTrimStart(Math.max(0, v));
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="End (s)"
                          type="number"
                          value={trimEnd}
                          inputProps={{
                            min: trimStart + 0.5,
                            max: videoDuration,
                            step: 0.5,
                          }}
                          onChange={(e) => {
                            const v = Math.max(
                              parseFloat(e.target.value) || 0,
                              trimStart + 0.5,
                            );
                            setTrimEnd(Math.min(videoDuration, v));
                          }}
                        />
                      </Grid>
                    </Grid>

                    {!videoDuration && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                        mt={1}
                      >
                        Loading video duration…
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Paper>

              {/* ── TTS Toolbar ── */}
              {captions.length > 0 && (
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{ mb: 2, p: 1.5, bgcolor: alpha("#0ea5e9", 0.04), borderRadius: 2, borderColor: alpha("#0ea5e9", 0.2) }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <RecordVoiceOverIcon fontSize="small" sx={{ color: "#0ea5e9" }} />
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ color: "#0369a1" }}
                    >
                      Text-to-Speech
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 140 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Voice</InputLabel>
                        <Select
                          label="Voice"
                          value={selectedVoice}
                          onChange={(e) => setSelectedVoice(e.target.value)}
                          disabled={ttsVoicesLoading || ttsVoices.length === 0}
                          sx={{ fontSize: 13 }}
                        >
                          {ttsVoicesLoading ? (
                            <MenuItem value="" disabled>
                              Loading voices…
                            </MenuItem>
                          ) : ttsVoices.length === 0 ? (
                            <MenuItem value="" disabled>
                              No voices available
                            </MenuItem>
                          ) : (
                            ttsVoices.map((v) => (
                              <MenuItem key={v.voice_id} value={v.voice_id}>
                                {v.name}
                                {v.category && v.category !== "premade" && (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{ ml: 0.5 }}
                                  >
                                    ({v.category})
                                  </Typography>
                                )}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Box>
                    <Tooltip title="Generate audio for all captions using the selected voice">
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={
                            bulkTtsLoading ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <GraphicEqIcon />
                            )
                          }
                          onClick={generateAllTts}
                          disabled={
                            bulkTtsLoading ||
                            !selectedVoice ||
                            captions.length === 0
                          }
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {bulkTtsLoading ? "Generating…" : "Get All Audio"}
                        </Button>
                      </span>
                    </Tooltip>
                    {/* Audio coverage badge */}
                    {captions.length > 0 && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {
                          Object.values(captionAudios).filter(
                            (a) => a?.url && !a?.stale,
                          ).length
                        }
                        /{captions.length} ready
                      </Typography>
                    )}
                  </Box>
                  {bulkTtsLoading && (
                    <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
                  )}
                </Paper>
              )}

              {/* ── Background Music ── */}
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  mb: 2,
                  bgcolor: bgMusic?.url ? alpha("#10b981", 0.03) : "white",
                  borderRadius: 2,
                  overflow: "hidden",
                  borderColor: bgMusic?.url ? alpha("#10b981", 0.3) : "#e2e8f0",
                }}
              >
                {/* Header row — click to collapse/expand */}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  onClick={() => setBgMusicOpen((v) => !v)}
                  sx={{
                    px: 1.5, py: 1.25,
                    cursor: "pointer", userSelect: "none",
                    borderBottom: bgMusicOpen ? "1px solid" : "none",
                    borderColor: "#e2e8f0",
                    bgcolor: bgMusicOpen ? alpha("#10b981", 0.04) : "transparent",
                    "&:hover": { bgcolor: alpha("#10b981", 0.07) },
                    transition: "background 0.15s",
                  }}
                >
                  <LibraryMusicIcon fontSize="small" sx={{ color: "#10b981" }} />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: "#059669" }}
                  >
                    Background Music
                  </Typography>
                  {bgMusic?.url && !bgMusicOpen && (
                    <CheckCircleIcon
                      fontSize="small"
                      color="success"
                      sx={{ ml: 0.5 }}
                    />
                  )}
                  {/* Tabs — stop propagation so clicks don't toggle collapse */}
                  <Box
                    sx={{ ml: "auto", display: "flex", gap: 0.5 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      size="small"
                      variant={
                        musicTab === "generate" ? "contained" : "outlined"
                      }
                      color="secondary"
                      startIcon={<MusicNoteIcon />}
                      onClick={() => setMusicTab("generate")}
                      sx={{ fontSize: 11, py: 0.25, px: 1 }}
                    >
                      Generate
                    </Button>
                    <Button
                      size="small"
                      variant={
                        musicTab === "history" ? "contained" : "outlined"
                      }
                      color="secondary"
                      startIcon={<HistoryIcon />}
                      onClick={() => {
                        setMusicTab("history");
                        if (!musicHistory.length) fetchMusicHistory();
                      }}
                      sx={{ fontSize: 11, py: 0.25, px: 1 }}
                    >
                      History
                    </Button>
                  </Box>
                  {bgMusicOpen ? (
                    <ExpandLessIcon fontSize="small" color="action" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" color="action" />
                  )}
                </Box>

                <Collapse in={bgMusicOpen}>
                  <Box sx={{ p: 1.5 }}>
                    {/* Generate tab */}
                    {musicTab === "generate" && (
                      <Box>
                        <Box display="flex" gap={1} alignItems="flex-start">
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="e.g. upbeat electronic background, calm piano, cinematic orchestra…"
                            value={musicPrompt}
                            onChange={(e) => setMusicPrompt(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !musicGenerating)
                                generateBackgroundMusic();
                            }}
                            multiline
                            maxRows={2}
                            sx={{ fontSize: 13 }}
                          />
                          <Tooltip title="ElevenLabs generates up to 22s of music, which loops to fill the full video duration">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                onClick={generateBackgroundMusic}
                                disabled={
                                  musicGenerating || !musicPrompt.trim()
                                }
                                startIcon={
                                  musicGenerating ? (
                                    <CircularProgress
                                      size={14}
                                      color="inherit"
                                    />
                                  ) : (
                                    <MusicNoteIcon />
                                  )
                                }
                                sx={{ whiteSpace: "nowrap" }}
                              >
                                {musicGenerating ? "Generating…" : "Generate"}
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          Generates up to 22s — loops automatically to fill the
                          full video duration
                        </Typography>
                      </Box>
                    )}

                    {/* History tab */}
                    {musicTab === "history" && (
                      <Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={0.5}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={
                              musicHistoryLoading ? (
                                <CircularProgress size={12} color="inherit" />
                              ) : (
                                <HistoryIcon />
                              )
                            }
                            onClick={fetchMusicHistory}
                            disabled={musicHistoryLoading}
                          >
                            {musicHistoryLoading
                              ? "Loading…"
                              : "Refresh History"}
                          </Button>
                        </Box>
                        {musicHistory.length === 0 && !musicHistoryLoading && (
                          <Typography variant="caption" color="textSecondary">
                            No sound effects found in ElevenLabs history yet.
                          </Typography>
                        )}
                        <Box
                          sx={{
                            maxHeight: 140,
                            overflowY: "auto",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {musicHistory.map((item) => (
                            <Box
                              key={item.id}
                              display="flex"
                              alignItems="center"
                              gap={1}
                              sx={{
                                p: 0.75,
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor:
                                  bgMusic?.prompt === item.text
                                    ? "secondary.main"
                                    : "grey.300",
                                bgcolor:
                                  bgMusic?.prompt === item.text
                                    ? "secondary.50"
                                    : "white",
                                cursor: "pointer",
                                "&:hover": { borderColor: "secondary.light" },
                              }}
                              onClick={() => selectHistoryMusic(item)}
                            >
                              <MusicNoteIcon
                                fontSize="small"
                                color="secondary"
                              />
                              <Box flex={1} minWidth={0}>
                                <Typography
                                  variant="caption"
                                  noWrap
                                  title={item.text}
                                >
                                  {item.text || "(no prompt)"}
                                </Typography>
                                {item.date && (
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{ display: "block", fontSize: 10 }}
                                  >
                                    {new Date(
                                      item.date * 1000,
                                    ).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Box>
                              {bgMusic?.prompt === item.text && (
                                <CheckCircleIcon
                                  fontSize="small"
                                  color="secondary"
                                />
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Loaded music — shown in both tabs when music is selected */}
                    {bgMusic?.url && (
                      <Box
                        sx={{
                          mt: 1.5,
                          pt: 1,
                          borderTop: "1px solid",
                          borderColor: "grey.200",
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          flexWrap="wrap"
                        >
                          <CheckCircleIcon fontSize="small" color="success" />
                          <Typography
                            variant="caption"
                            color="success.main"
                            fontWeight={600}
                            sx={{ flex: 1, minWidth: 0 }}
                            noWrap
                          >
                            {bgMusic.prompt}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {bgMusic.duration?.toFixed(1)}s (loops)
                          </Typography>
                          <Tooltip title="Remove background music">
                            <IconButton
                              size="small"
                              onClick={() => setBgMusic(null)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {/* Volume control */}
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mt={0.75}
                        >
                          <VolumeOffIcon fontSize="small" color="action" />
                          <Slider
                            size="small"
                            value={bgMusicVolume}
                            onChange={(_, v) => setBgMusicVolume(v)}
                            min={0}
                            max={100}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${v}%`}
                            color="secondary"
                            sx={{ flex: 1 }}
                          />
                          <VolumeUpIcon fontSize="small" color="action" />
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ minWidth: 32, textAlign: "right" }}
                          >
                            {bgMusicVolume}%
                          </Typography>
                        </Box>
                        {/* Speed control */}
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mt={0.75}
                        >
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            Speed:
                          </Typography>
                          {[0.5, 0.75, 1, 1.25, 1.5, 2].map((s) => (
                            <Chip
                              key={s}
                              label={`${s}x`}
                              size="small"
                              color={
                                bgMusicSpeed === s ? "secondary" : "default"
                              }
                              variant={
                                bgMusicSpeed === s ? "filled" : "outlined"
                              }
                              onClick={() => setBgMusicSpeed(s)}
                              sx={{
                                cursor: "pointer",
                                fontSize: "0.7rem",
                                height: 22,
                              }}
                            />
                          ))}
                        </Box>
                        {/* Preview player */}
                        <audio
                          controls
                          src={bgMusic.url}
                          style={{ width: "100%", height: 32, marginTop: 4 }}
                        />
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Paper>

              <Collapse in={showPromptField}>
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.300",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={0.75}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Generation prompt — use{" "}
                      <code style={{ fontSize: "0.7rem" }}>
                        {"{{duration}}"}
                      </code>
                      ,{" "}
                      <code style={{ fontSize: "0.7rem" }}>{"{{title}}"}</code>,{" "}
                      <code style={{ fontSize: "0.7rem" }}>
                        {"{{description}}"}
                      </code>
                      ,{" "}
                      <code style={{ fontSize: "0.7rem" }}>
                        {"{{trim_context}}"}
                      </code>{" "}
                      as dynamic tokens
                    </Typography>
                    <Box display="flex" gap={1} flexShrink={0} ml={1}>
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{
                          cursor: "pointer",
                          textDecoration: "underline",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => {
                          setCustomPrompt(buildDefaultTemplate());
                          localStorage.removeItem(CAPTION_PROMPT_LS_KEY);
                          toast.info("Reset to default template");
                        }}
                      >
                        Reset to default
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ·
                      </Typography>
                      <Typography
                        variant="caption"
                        color="success.main"
                        sx={{
                          cursor: "pointer",
                          textDecoration: "underline",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => {
                          if (customPrompt.trim()) {
                            localStorage.setItem(
                              CAPTION_PROMPT_LS_KEY,
                              customPrompt.trim(),
                            );
                            toast.success("Saved as your default template");
                          }
                        }}
                      >
                        Set as my default
                      </Typography>
                    </Box>
                  </Box>
                  {customPrompt.trim() &&
                    !customPrompt.includes("{{duration}}") && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        Tip: add{" "}
                        <code style={{ fontSize: "0.7rem" }}>
                          {"{{duration}}"}
                        </code>{" "}
                        so the AI knows the clip length
                      </Typography>
                    )}
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={8}
                    size="small"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    sx={{ fontFamily: "monospace" }}
                  />
                </Box>
              </Collapse>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
                sx={{ pt: 0.5 }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 4, height: 20, borderRadius: 2, bgcolor: "#4338ca" }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} color="#312e81">
                      Captions
                    </Typography>
                    {captions.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {captions
                          .reduce((sum, c) => sum + c.duration, 0)
                          .toFixed(1)}
                        s total
                        {clipDuration > 0 &&
                          ` / ${clipDuration.toFixed(1)}s clip`}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box display="flex" gap={1} alignItems="center">
                  <Tooltip
                    title={
                      showPromptField ? "Hide prompt" : "Edit generation prompt"
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowPromptField((prev) => {
                          if (!prev && !customPrompt.trim()) {
                            const saved = localStorage.getItem(
                              CAPTION_PROMPT_LS_KEY,
                            );
                            setCustomPrompt(saved || buildDefaultTemplate());
                          }
                          return !prev;
                        });
                      }}
                      color={
                        showPromptField || customPrompt.trim()
                          ? "primary"
                          : "default"
                      }
                    >
                      <TuneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      generatingCaptions ? (
                        <CircularProgress size={14} />
                      ) : (
                        <AutoFixHighIcon />
                      )
                    }
                    onClick={generateCaptions}
                    disabled={generatingCaptions || videoDuration === 0}
                  >
                    {generatingCaptions ? "Generating…" : "Auto-generate"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addCaption}
                    disabled={videoDuration === 0}
                  >
                    Add
                  </Button>
                  {captions.length > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<ClearAllIcon />}
                      onClick={() => setCaptions([])}
                    >
                      Clear all
                    </Button>
                  )}
                </Box>
              </Box>

              {captions.length === 0 ? (
                <Box
                  sx={{
                    border: "2px dashed",
                    borderColor: alpha("#4338ca", 0.3),
                    borderRadius: 2, p: 4, textAlign: "center",
                    bgcolor: alpha("#4338ca", 0.02),
                  }}
                >
                  <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: alpha("#4338ca", 0.1),
                    display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                    <AutoFixHighIcon sx={{ fontSize: 28, color: "#4338ca" }} />
                  </Box>
                  <Typography variant="subtitle2" fontWeight={600} color="#312e81" mb={0.5}>
                    No captions yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click <strong>Auto-generate</strong> to create captions from the video's title and description, or add them manually.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 440, overflowY: "auto", pr: 0.5 }}>
                  {(() => {
                    return captions.map((cap, index) => {
                      const isActive =
                        currentTime >= cap.startTime &&
                        currentTime < cap.startTime + cap.duration;

                      return (
                        <Paper
                          key={cap.id}
                          elevation={isActive ? 2 : 0}
                          variant={isActive ? "elevation" : "outlined"}
                          sx={{
                            mb: 1.5,
                            borderColor: isActive ? alpha("#4338ca", 0.4) : "#e2e8f0",
                            transition: "all 0.2s",
                            bgcolor: isActive ? alpha("#4338ca", 0.02) : "white",
                            borderRadius: 2,
                            overflow: "hidden",
                            display: "flex",
                          }}
                        >
                          {/* Left accent bar */}
                          <Box sx={{ width: 4, flexShrink: 0,
                            bgcolor: isActive ? "#4338ca" : alpha("#4338ca", 0.2),
                            transition: "background 0.2s",
                          }} />
                          <Box sx={{ p: 2, flex: 1, minWidth: 0 }}>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <Box display="flex" alignItems="center" gap={0.75}>
                              <Chip
                                label={`Caption ${index + 1}`}
                                size="small"
                                sx={isActive ? { bgcolor: alpha("#4338ca",0.1), color: "#4338ca", fontWeight:700, border:"1px solid "+alpha("#4338ca",0.3) } : {}}
                                color={isActive ? undefined : "default"}
                              />
                              <Tooltip title="Track (layer) this caption renders on">
                                <Chip
                                  label={`Track ${cap.track ?? defaultCaptionTrack}`}
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                />
                              </Tooltip>
                            </Box>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeCaption(cap.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            size="small"
                            label="Caption text"
                            value={cap.text}
                            onChange={(e) =>
                              updateCaption(cap.id, "text", e.target.value)
                            }
                            sx={{ mb: 1 }}
                          />

                          <Grid container spacing={1}>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Start time (s)"
                                type="number"
                                inputProps={{
                                  min: 0,
                                  step: 0.1,
                                }}
                                helperText="When the caption appears"
                                value={cap.startTime}
                                onChange={(e) =>
                                  updateCaption(
                                    cap.id,
                                    "startTime",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Duration (s)"
                                type="number"
                                inputProps={{
                                  min: 0.1,
                                  step: 0.1,
                                }}
                                helperText="How long it stays on screen"
                                value={cap.duration}
                                onChange={(e) =>
                                  updateCaption(
                                    cap.id,
                                    "duration",
                                    parseFloat(e.target.value) || 1,
                                  )
                                }
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Track (layer)"
                                type="number"
                                inputProps={{
                                  min: 1,
                                  step: 1,
                                }}
                                helperText={
                                  reservedTracksLabel
                                    ? `Avoid ${reservedTracksLabel} & ≥${TRACK_CAPTION_AUDIO_BASE}`
                                    : "Empty or caption track only"
                                }
                                value={cap.track ?? defaultCaptionTrack}
                                onChange={(e) =>
                                  updateCaption(cap.id, "track", e.target.value)
                                }
                              />
                            </Grid>
                          </Grid>

                          {/* ── Per-caption TTS row ── */}
                          {(() => {
                            const audio = captionAudios[cap.id];
                            const hasAudio = audio?.url && !audio?.stale;
                            const isStale = audio?.stale && audio?.url;
                            return (
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mt={1.5}
                                pt={1}
                                sx={{
                                  borderTop: "1px solid",
                                  borderColor: "divider",
                                  flexWrap: "wrap",
                                }}
                              >
                                {/* Generate / regenerate button */}
                                <Tooltip
                                  title={
                                    !selectedVoice
                                      ? "Select a voice above first"
                                      : hasAudio
                                        ? "Regenerate audio"
                                        : isStale
                                          ? "Text changed — regenerate audio"
                                          : "Generate audio for this caption"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        generateCaptionTts(cap.id, cap.text)
                                      }
                                      disabled={
                                        audio?.loading ||
                                        !selectedVoice ||
                                        !cap.text?.trim()
                                      }
                                      color={
                                        hasAudio
                                          ? "success"
                                          : isStale
                                            ? "warning"
                                            : "default"
                                      }
                                    >
                                      {audio?.loading ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <RecordVoiceOverIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                {/* Status: ready */}
                                {hasAudio && (
                                  <>
                                    <GraphicEqIcon
                                      fontSize="small"
                                      color="success"
                                      sx={{ opacity: 0.8 }}
                                    />
                                    <Typography
                                      variant="caption"
                                      color="success.main"
                                      sx={{ whiteSpace: "nowrap" }}
                                    >
                                      {audio.audioDuration != null
                                        ? `${audio.audioDuration}s`
                                        : "ready"}
                                    </Typography>
                                    {/* Native audio preview — key forces remount when URL changes */}
                                    <Box
                                      component="audio"
                                      key={audio.url}
                                      src={audio.url}
                                      controls
                                      sx={{
                                        height: 28,
                                        maxWidth: 160,
                                        flex: "0 1 160px",
                                        "&::-webkit-media-controls-panel": {
                                          bgcolor: "transparent",
                                        },
                                      }}
                                    />
                                    {/* Delete caption audio */}
                                    <Tooltip title="Remove audio for this caption">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                          if (audioRefsMap.current[cap.id]) {
                                            audioRefsMap.current[
                                              cap.id
                                            ].pause();
                                            audioRefsMap.current[cap.id].src =
                                              "";
                                            delete audioRefsMap.current[cap.id];
                                          }
                                          setCaptionAudios((prev) => {
                                            const next = { ...prev };
                                            delete next[cap.id];
                                            return next;
                                          });
                                          setCaptionAudioSpeeds((prev) => {
                                            const next = { ...prev };
                                            delete next[cap.id];
                                            return next;
                                          });
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                                {/* Per-caption speed — shown in its own row below */}
                                {hasAudio && (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                    sx={{ width: "100%", mt: 0.5 }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      sx={{ whiteSpace: "nowrap", mr: 0.5 }}
                                    >
                                      Speed:
                                    </Typography>
                                    {[0.5, 0.75, 1, 1.25, 1.5, 2].map((s) => {
                                      const current =
                                        captionAudioSpeeds[cap.id] ?? 1;
                                      return (
                                        <Chip
                                          key={s}
                                          label={`${s}x`}
                                          size="small"
                                          color={
                                            current === s
                                              ? "primary"
                                              : "default"
                                          }
                                          variant={
                                            current === s
                                              ? "filled"
                                              : "outlined"
                                          }
                                          onClick={() =>
                                            setCaptionAudioSpeeds((prev) => ({
                                              ...prev,
                                              [cap.id]: s,
                                            }))
                                          }
                                          sx={{
                                            cursor: "pointer",
                                            fontSize: "0.7rem",
                                            height: 22,
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                )}

                                {/* Status: stale */}
                                {isStale && (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                  >
                                    <WarningAmberIcon
                                      fontSize="small"
                                      color="warning"
                                    />
                                    <Typography
                                      variant="caption"
                                      color="warning.main"
                                    >
                                      Text changed
                                    </Typography>
                                  </Box>
                                )}

                                {/* Status: error */}
                                {audio?.error && !audio?.loading && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                      maxWidth: 180,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {audio.error}
                                  </Typography>
                                )}

                                {/* No audio yet and not loading */}
                                {!audio?.url &&
                                  !audio?.loading &&
                                  !audio?.error && (
                                    <Typography
                                      variant="caption"
                                      color="textDisabled"
                                    >
                                      No audio
                                    </Typography>
                                  )}
                              </Box>
                            );
                          })()}
                          </Box>{/* end card inner Box */}
                        </Paper>
                      );
                    });
                  })()}
                </Box>
              )}

              {/* Caption style badge */}
              <Box mt={1.5} p={1.5} sx={{ bgcolor: alpha("#4338ca", 0.04), borderRadius: 2,
                border: "1px solid", borderColor: alpha("#4338ca", 0.12) }}>
                <Typography variant="caption" color="text.secondary">
                  <strong style={{ color: "#4338ca" }}>Caption style</strong>
                  &nbsp;·&nbsp;font: {captionStyle.font_family || "default"}
                  &nbsp;·&nbsp;size: {captionStyle.font_size}
                  &nbsp;·&nbsp;
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, verticalAlign: "middle" }}>
                    color:
                    <Box component="span" sx={{ display: "inline-block", width: 12, height: 12,
                      bgcolor: captionStyle.color, border: "1px solid #ccc", borderRadius: "3px",
                      verticalAlign: "middle", ml: 0.25 }} />
                    {captionStyle.color}
                  </Box>
                </Typography>
              </Box>
            </Grid>

            {/* Right: Creatomate Preview Player */}
            <Grid item xs={12} md={7}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 4, height: 20, borderRadius: 2, bgcolor: "#4338ca" }} />
                  <Typography variant="subtitle1" fontWeight={700} color="#312e81">
                    Live Preview
                  </Typography>
                </Box>
                {selectedTemplate && (
                  <Button size="small" variant="outlined" onClick={() => setActiveStep(0)}
                    sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600,
                      borderColor: alpha("#4338ca", 0.4), color: "#4338ca",
                      "&:hover": { borderColor: "#4338ca", bgcolor: alpha("#4338ca", 0.06) } }}>
                    Change template
                  </Button>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Rendered by Creatomate's engine — exact match to the final output.
                Caption cards on the left highlight as playback advances. Drag elements to reposition.
              </Typography>

              {/* Creatomate preview player — mounts once, source is updated via setSource() */}
              <CreatomatePreviewPlayer
                source={previewSource}
                onTimeChange={handlePreviewTimeChange}
                onStateChange={handlePreviewStateChange}
                onPlayPauseChange={handlePreviewPlayPause}
                video_duration={clipDuration}
                trim_start={trimStart}
                onCaptureThumbnail={onThumbnailSave ? async (time, liveSource) => {
                  if (thumbnailCapturing) return;
                  setThumbnailCapturing(true);
                  try {
                    // Prefer the SDK's live source so its duration matches the shown frame
                    const sourceToSend = liveSource || previewSource;
                    // Safety: never request a time past the composition end, otherwise
                    // Creatomate clamps snapshot_time to the duration → wrong frame.
                    const dur = Number(sourceToSend?.duration);
                    let captureTime = time;
                    if (dur > 0 && captureTime > dur) captureTime = Math.max(0, dur - 0.05);
                    const response = await api.post(
                      "videos/creatomate-snapshot/",
                      { source: sourceToSend, time: captureTime.toFixed(2) },
                      { timeout: 100000 },
                    );
                    setThumbnailCapturedFrame(response.data.data_uri);
                    setThumbnailEditorOpen(true);
                  } catch (err) {
                    toast.error("Failed to capture frame — please try again.");
                  } finally {
                    setThumbnailCapturing(false);
                  }
                } : undefined}
                isCapturingThumbnail={thumbnailCapturing}
              />

              {/* TikTok cover frame — pause on the frame, then set its time as the cover */}
              {onCoverTimestampSave && (
                <Box mt={1.5} display="flex" alignItems="center" gap={1} flexWrap="wrap"
                  sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: alpha("#010101", 0.04),
                    border: "1px solid", borderColor: alpha("#010101", 0.15) }}>
                  <AccessTimeIcon sx={{ fontSize: 16, color: "#010101" }} />
                  <Typography variant="caption" fontWeight={700} color="#010101">
                    TikTok Cover Frame
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AccessTimeIcon sx={{ fontSize: "14px !important" }} />}
                    onClick={() => {
                      const t = Math.round(currentTime);
                      onCoverTimestampSave(t);
                      toast.success(`Cover frame set at ${formatSeconds(t)}`);
                    }}
                    sx={{ textTransform: "none", fontWeight: 600, fontSize: 11, py: 0.25,
                      color: "#010101", borderColor: alpha("#010101", 0.4),
                      "&:hover": { borderColor: "#010101", bgcolor: alpha("#010101", 0.06) } }}
                  >
                    Set as Cover ({formatSeconds(currentTime)})
                  </Button>
                  {coverTimestamp !== null && coverTimestamp !== undefined && coverTimestamp !== "" && (
                    <Chip
                      size="small"
                      label={`Cover @ ${formatSeconds(coverTimestamp)}`}
                      onDelete={() => onCoverTimestampSave(null)}
                      sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Pause the preview on the frame you want, then set it.
                  </Typography>
                </Box>
              )}

              {/* Selected template info */}
              {selectedTemplate && (
                <Box mt={1.5} display="flex" alignItems="center" gap={1}
                  sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: alpha("#4338ca", 0.06),
                    border: "1px solid", borderColor: alpha("#4338ca", 0.15), width: "fit-content" }}>
                  <EditIcon fontSize="small" sx={{ color: "#4338ca", fontSize: 14 }} />
                  <Typography variant="caption" fontWeight={600} color="#4338ca">
                    {selectedTemplate.name}
                  </Typography>
                </Box>
              )}

              {/* ── Video Narration ── */}
              <Paper
                elevation={0}
                variant="outlined"
                sx={{ mt: 2, p: 1.5,
                  bgcolor: narrationAudio?.url && !narrationAudio?.stale ? alpha("#0ea5e9", 0.04) : "white",
                  borderRadius: 2,
                  borderColor: narrationAudio?.url && !narrationAudio?.stale ? alpha("#0ea5e9", 0.3) : "#e2e8f0",
                }}
              >
                {/* Header */}
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <RecordVoiceOverIcon fontSize="small" sx={{ color: "#0ea5e9" }} />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: "#0369a1" }}
                  >
                    Video Narration
                  </Typography>
                  <Tooltip title="Generate a narration script from this video's title and description using AI">
                    <span style={{ marginLeft: "auto" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={
                          storyGenerating ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : (
                            <AutoFixHighIcon fontSize="small" />
                          )
                        }
                        onClick={generateStory}
                        disabled={storyGenerating}
                        sx={{ fontSize: 11, py: 0.25, px: 1 }}
                      >
                        {storyGenerating ? "Generating…" : "Generate Story"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>

                {/* Script text area */}
                <TextField
                  multiline
                  fullWidth
                  minRows={3}
                  maxRows={6}
                  size="small"
                  placeholder="Write or generate a narration script for the video…"
                  value={narrationText}
                  onChange={(e) => {
                    setNarrationText(e.target.value);
                    if (narrationAudio?.url)
                      setNarrationAudio((prev) => ({ ...prev, stale: true }));
                  }}
                  sx={{ bgcolor: "white", fontSize: 13 }}
                />

                {/* Generate audio row */}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mt={1}
                  flexWrap="wrap"
                >
                  <Tooltip
                    title={
                      !selectedVoice
                        ? "Select a voice in the Text-to-Speech section first"
                        : ""
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={
                          narrationAudioGenerating ? (
                            <CircularProgress size={13} color="inherit" />
                          ) : (
                            <GraphicEqIcon />
                          )
                        }
                        onClick={generateNarrationAudio}
                        disabled={
                          narrationAudioGenerating ||
                          !narrationText.trim() ||
                          !selectedVoice
                        }
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {narrationAudioGenerating
                          ? "Generating…"
                          : narrationAudio?.url
                            ? "Regenerate Audio"
                            : "Generate Audio"}
                      </Button>
                    </span>
                  </Tooltip>
                  {narrationAudio?.url && !narrationAudio?.stale && (
                    <>
                      <CheckCircleIcon fontSize="small" color="success" />
                      <Typography variant="caption" color="success.main">
                        {narrationAudio.duration?.toFixed(1)}s ready
                      </Typography>
                    </>
                  )}
                  {narrationAudio?.stale && (
                    <Typography variant="caption" color="warning.main">
                      Script changed — regenerate audio
                    </Typography>
                  )}
                  {narrationAudio?.url && (
                    <Tooltip title="Remove narration audio">
                      <IconButton
                        size="small"
                        onClick={() => setNarrationAudio(null)}
                        color="error"
                        sx={{ ml: "auto" }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {/* Volume + mini player (when audio is ready) */}
                {narrationAudio?.url && !narrationAudio?.stale && (
                  <Box mt={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <VolumeOffIcon fontSize="small" color="action" />
                      <Slider
                        size="small"
                        value={narrationVolume}
                        onChange={(_, v) => setNarrationVolume(v)}
                        min={0}
                        max={100}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}%`}
                        color="primary"
                        sx={{ flex: 1 }}
                      />
                      <VolumeUpIcon fontSize="small" color="action" />
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ minWidth: 32, textAlign: "right" }}
                      >
                        {narrationVolume}%
                      </Typography>
                    </Box>
                    {/* Speed control */}
                    <Box display="flex" alignItems="center" gap={1} mt={0.75}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Speed:
                      </Typography>
                      {[0.5, 0.75, 1, 1.25, 1.5, 2].map((s) => (
                        <Chip
                          key={s}
                          label={`${s}x`}
                          size="small"
                          color={narrationSpeed === s ? "primary" : "default"}
                          variant={narrationSpeed === s ? "filled" : "outlined"}
                          onClick={() => setNarrationSpeed(s)}
                          sx={{
                            cursor: "pointer",
                            fontSize: "0.7rem",
                            height: 22,
                          }}
                        />
                      ))}
                    </Box>
                    <audio
                      controls
                      src={narrationAudio.url}
                      style={{ width: "100%", height: 32, marginTop: 4 }}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <Divider sx={{ borderColor: "#e2e8f0" }} />

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "#f8fafc", gap: 1 }}>
        {activeStep === 0 && (
          <>
            <Button onClick={onClose} variant="outlined"
              sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "text.secondary" }}>
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              disabled={!selectedTemplate}
              onClick={() => setActiveStep(1)}
              sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 700,
                background: selectedTemplate ? "linear-gradient(135deg, #312e81, #4338ca)" : undefined,
                px: 3 }}
            >
              Next: Edit Captions →
            </Button>
          </>
        )}

        {activeStep === 1 && (
          <>
            {preselectedTemplate ? (
              <Button onClick={onClose} variant="outlined"
                sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "text.secondary" }}>
                Cancel
              </Button>
            ) : (
              <Button onClick={() => setActiveStep(0)} variant="outlined"
                sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600,
                  borderColor: alpha("#4338ca", 0.4), color: "#4338ca",
                  "&:hover": { borderColor: "#4338ca", bgcolor: alpha("#4338ca", 0.06) } }}>
                ← Back
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<MovieFilterIcon />}
              onClick={handleRenderAndSave}
              sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 700, px: 3,
                background: "linear-gradient(135deg, #065f46, #10b981)",
                "&:hover": { background: "linear-gradient(135deg, #047857, #059669)" } }}
            >
              Save Edit
            </Button>
          </>
        )}
      </DialogActions>

      {/* Thumbnail Studio — opened from the "Capture Thumbnail" button in the preview player */}
      {onThumbnailSave && (
        <MRSSThumbnailCreator
          open={thumbnailEditorOpen}
          setOpen={setThumbnailEditorOpen}
          url={effectiveVideoUrl}
          capturedFrame={thumbnailCapturedFrame}
          onSave={onThumbnailSave}
        />
      )}
    </Dialog>
  );
}

export default CreatomateVideoEditDialog;
